import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, AlertTriangle, Link2, Link2Off, ScanLine, Copy, Plus, Cpu,
  Monitor, CreditCard, Package, LayoutGrid, MapPin, Lock,
  RotateCcw,
} from 'lucide-react';
import { api } from '../../services/apiService';
import { hardwareApi, UNIT_STATUS_LABEL, formFactorLabel, type DeviceUnitRow, type DeviceModelRow } from '../../services/hardwareApi';
import {
  resolvePaymentConfig, getPaymentConfig, paymentConfigOptionsFor,
  activeSheetFor, type VarSheet,
} from '@platform/shared-types';
import { toastService } from '../../services/toastService';
import { useDataVersion } from '../../services/dataChangeBus';
// The register gate reads the OS stamp and the checklist, which is why it
// lives beside them rather than here.
import { registerBlockReason, registerGate, type RegisterGate } from './UnitProvisioning';
// One device, at a glance — opened from the device rows in these lanes and
// from the list at the top of the card, so both describe it identically.
import UnitQuickView from './UnitQuickView';
// What terminal type a catalog device provisions as — the one place the
// device's screen type is turned into the register's `terminalType`.
import { provisionTypeForModel, provisionTypeFromFormFactor } from '../pos/provisionType';
import ShimmerImage from '../../components/ShimmerImage';
import Barcode from '../../components/Barcode';
import { Toggle } from '../../components/pos/CardProcessingOptionsCard';
/* ONE approval section, shared with the merchant worksheet — the two lanes
   must not grow separate ideas of what "approved" means for a location. */
import SurchargeApprovalSection from '../../components/pos/SurchargeApprovalSection';
import {
  RegisterState, registerFromResponses, hasStoredFlags, assignTender,
  DEVICE_CLASS_TO_METHOD, unknownReaderMessage,
} from '../../components/pos/payment-types/model';
import { RegisterPaymentTypes, readerInfoFor } from '../../components/pos/payment-types/RegisterPaymentTypes';
import { PaymentTypesSaveHub, usePaymentTypesHub } from '../../components/pos/payment-types/SaveHub';
import { RubyTillPayDevicePanel } from '../../components/pos/RubyTillPayDevicePanel';
import {
  RtpayModeCards, RtpayPairingCodePanel, RTPAY_CLASS, isRtpayDevice, isTapToPayDevice, isValidRtpayPin,
  type RtpayRegisterMode, type RtpayPairingCode,
} from '../../components/pos/RubyTillPayRegisterFields';
/*
  THE REAL BUTTON LAYOUT CONTROL, not a second copy of it.

  This step used to offer three named templates — 'Retail — grid', 'Retail —
  categories', 'Blank' — which wrote a `layoutTemplate` STRING that nothing in
  the platform ever read. Three choices, none of which put a single button on a
  till: the register's `buttonLayout` stayed absent, so the merchant opened
  exactly the empty screen this step exists to prevent.

  There is only one real mechanism (`buttonLayout.available`), and the tab on
  the terminal page already implements it against the tenant's package features.
  Reusing it means the 30-button registry, the package gating and the
  round-trip of the Keypad Designer's own fields live in ONE place — which is
  the whole reason that registry carries a "keep in sync" warning.
*/
import PosButtonLayoutTab from '../../components/pos/PosButtonLayoutTab';
import PosSetupRunner from './PosSetupRunner';
import { readerBySerial } from './readerMatch';
import SearchableDropdown from '../../components/SearchableDropdown';
import ReportProblemButton from './ReportProblemButton';
import {
  Disclosure, DisclosureBody, StageChip, StepRow, CoupleHeader,
} from './stageShell';
import { RubyLoader } from '../../components/RubyLoader';
import { usableTerminalCode, formatExpiry } from '../../components/pos/TerminalNewCodeModal';

/**
 * THE FOUR LANES' OWN PANELS, and the merge.
 *
 * Every one of these does its work through the SAME endpoint the standalone
 * screen uses — `posTerminals.provision`, `posNmi.registerEmvDeviceToLocation`,
 * `posNmi.putPaymentTypes`, `posConfig.updateRegister`. Nothing here is a
 * second way to write the same record: the order screen knows WHAT is needed
 * (it has the units and the lines), the existing services know HOW.
 *
 * They fetch on mount, and mount only when their lane is opened and their
 * stage expanded — which is why the order screen no longer pulls four lanes'
 * worth of data on arrival.
 */

/* ── shared furniture ───────────────────────────────────────────────────── */

const CATEGORY_ICON: Record<string, React.FC<any>> = {
  pos_terminal: Monitor,
  payment_device: CreditCard,
};

const Busy: React.FC = () => (
  <RubyLoader variant="section" />
);

const Problem: React.FC<{ text: string; onRetry?: () => void }> = ({ text, onRetry }) => (
  <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3">
    <AlertTriangle size={15} className="mt-0.5 shrink-0 text-red-600" />
    <div className="text-[12.5px] text-red-800">
      {text}
      {onRetry && (
        <button onClick={onRetry} className="ml-2 font-semibold underline">Try again</button>
      )}
    </div>
  </div>
);

const Row: React.FC<{
  model?: DeviceModelRow;
  title: string;
  sub: string;
  done?: boolean;
  /**
   * Opens this device's quick view. ONLY the photo-and-name area is the
   * control — the rest of the row holds a text input and the action buttons,
   * and a row-wide click target would fire every time somebody aimed at one
   * of those.
   */
  onOpen?: () => void;
  children?: React.ReactNode;
}> = ({ model, title, sub, done, onOpen, children }) => {
  const Icon = CATEGORY_ICON[model?.category || ''] || Package;
  const identity = (
    <>
      <ShimmerImage
        src={model?.media?.heroImageUrl}
        alt={model?.modelName || title}
        width={38}
        height={38}
        objectFit="contain"
        className="shrink-0 rounded-lg border border-stone-200"
        fallbackBgColor="bg-stone-100"
        fallbackIcon={<Icon size={16} className="text-stone-400" />}
      />
      <div className="min-w-0 flex-1 text-left">
        <div className="truncate text-[12.5px] font-semibold text-stone-800">{title}</div>
        <div className="truncate font-mono text-[11px] text-stone-400">{sub}</div>
      </div>
    </>
  );
  return (
    <div className={`flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2.5 ${
      done ? 'border-stone-200 bg-white' : 'border-dashed border-stone-300 bg-stone-50/60'}`}
    >
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          title={`Quick info — ${sub}`}
          className="group flex min-w-[220px] flex-1 items-center gap-3 rounded-lg text-left hover:opacity-80"
        >
          {identity}
        </button>
      ) : (
        <div className="flex min-w-[220px] flex-1 items-center gap-3">{identity}</div>
      )}
      {children}
    </div>
  );
};

/**
 * WHAT THIS IS WAITING FOR, AND THAT IT IS WATCHING.
 *
 * The old gate was a flat amber sentence, which reads as a refusal — an
 * operator ticking boxes above had no way to tell whether this section knew
 * about it, or whether it would need a reload. So it now says the number of
 * tasks left, counts down as they are ticked, and carries a slow pulse: the
 * pulse is the part that says "I am watching the checklist", which is exactly
 * the thing somebody wants to know before they go and tick the last box.
 *
 * SINGULAR AT ONE. "Complete 1 pending task to unlock" is the sentence people
 * actually read at the end, and "1 tasks" undoes the care of everything above
 * it.
 *
 * The pulse is deliberately absent for `install` and `not_started`, because
 * neither unlocks by itself — nothing is going to happen while you watch, and
 * implying otherwise would be a small lie told continuously.
 */
const UnlockGate: React.FC<{ gate: RegisterGate }> = ({ gate }) => {
  const counting = gate.kind === 'checklist';
  const pct = gate.total > 0 ? Math.round((gate.done / gate.total) * 100) : 0;

  return (
    <div className="flex min-w-[260px] flex-1 items-center gap-2.5 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-2">
      <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
        {counting && (
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-60" />
        )}
        <Lock size={12} className="relative text-amber-700" />
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-[11.5px] font-semibold leading-tight text-amber-900">
          {counting
            ? `Complete ${gate.pending} pending task${gate.pending === 1 ? '' : 's'} to unlock`
            : gate.reason}
        </p>
        {counting && (
          <>
            <p className="mt-0.5 text-[10.5px] leading-tight text-amber-700/80">
              Unlocks by itself the moment the last box above is ticked.
            </p>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-amber-200">
                <i
                  className="block h-full rounded-full bg-amber-500 transition-all duration-500"
                  style={{ width: `${pct}%` }}
                />
              </span>
              <span className="shrink-0 text-[10px] font-semibold tabular-nums text-amber-800">
                {gate.done}/{gate.total}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const Chip: React.FC<{ tone?: 'ok' | 'wait' | 'bad'; children: React.ReactNode }> = ({ tone = 'wait', children }) => (
  <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
    tone === 'ok' ? 'bg-emerald-100 text-emerald-800'
      : tone === 'bad' ? 'bg-red-100 text-red-700'
      : 'bg-stone-200 text-stone-600'}`}
  >
    {children}
  </span>
);

const Action: React.FC<{
  onClick: () => void; busy?: boolean; disabled?: boolean; title?: string; children: React.ReactNode;
}> = ({ onClick, busy, disabled, title, children }) => (
  <button
    onClick={onClick}
    disabled={busy || disabled}
    title={title}
    className="inline-flex min-h-[36px] items-center justify-center gap-1.5 rounded-lg bg-rose-600 px-3 text-[11.5px] font-semibold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-50"
  >
    {busy && <RubyGemMark size={12} drawing color="currentColor" />}
    {children}
  </button>
);

interface LaneProps {
  /**
   * The checklist stages the CALLING screen draws.
   *
   * Passed through to the register gate so it counts the same steps the
   * operator can actually see and tick on that screen. Omitted on the
   * unfiltered views, where every step is on show and counting them all is
   * right.
   */
  stageIds?: readonly string[];
  tenantId: string;
  tenantName?: string;
  locationId?: string;
  locationName?: string;
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  onChanged: () => void;
}

/**
 * WHOSE STORE THIS IS.
 *
 * Both a register and a device pool belong to ONE LOCATION, and a tenant with
 * four shops gets four of each. Doing that work from an order screen without
 * the store's name on it is how a till gets provisioned into the wrong branch
 * — the order knows which location it is for, so it says so.
 */
export const ForLocation: React.FC<{
  tenantName?: string; locationName?: string; locationId?: string; what: string;
}> = ({ tenantName, locationName, locationId, what }) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl border border-stone-200 bg-stone-50 px-3 py-2 text-[12px]">
    <MapPin size={13} className="shrink-0 text-stone-400" />
    <span className="font-semibold text-stone-800">{tenantName || 'This tenant'}</span>
    <span className="text-stone-300">·</span>
    <span className={locationName ? 'text-stone-700' : 'font-mono text-[11.5px] text-stone-400'}>
      {locationName || locationId || 'no location on this order'}
    </span>
    <span className="ml-auto text-[11px] text-stone-400">{what} belong to this location only</span>
  </div>
);

/*
  The register gate lives in UnitProvisioning.tsx (`registerBlockReason`), with
  the OS gate and the checklist it reads — one rule, one file. The state set it
  replaces allowed 'provisioning', so a register could be minted from step one
  of twenty while the panel told the operator to "install RubyTill first".
*/

const modelOf = (models: DeviceModelRow[], id?: string) => models.find((m) => m.modelId === id);
const label = (u: DeviceUnitRow) => u.trackingId || u.serialNumber || u.unitId;

/**
 * THE REGISTER BELONGING TO A UNIT.
 *
 * `deviceUnitId` is the real link: `pos-terminal-provision.ts` writes it on the
 * PROVISION# row and activation carries it onto the REGISTER# row. It was
 * simply never projected by `pos-terminals-list`, so every caller here matched
 * on whether the register NAME contained the unit's tracking id instead.
 *
 * That guess failed the moment a register was named the way a shop actually
 * names one. A device coupled, provisioned and trading as "Front counter"
 * reported "has no register yet — create it on the POS devices lane first",
 * and the Payment types and Button layout steps refused to open for it.
 *
 * The name match stays as a FALLBACK, and only for registers with no
 * `deviceUnitId` at all — those minted before the field was written. A register
 * that carries the field is matched on it alone, so a name that happens to
 * contain another unit's tracking id can never claim it.
 */
export const registerForUnit = <T extends { registerName?: string | null; deviceUnitId?: string | null }>(
  terminals: T[] | null | undefined,
  unit: DeviceUnitRow,
): T | undefined => {
  const list = terminals || [];
  const linked = list.find((t) => t.deviceUnitId && t.deviceUnitId === unit.unitId);
  if (linked) return linked;
  return list.find((t) => !t.deviceUnitId && String(t.registerName || '').includes(label(unit)));
};

/**
 * EVERY register that belongs to any of these units — the many-unit form of
 * `registerForUnit`, same rule: a register carrying `deviceUnitId` matches on
 * it alone; the tracking-id-in-the-name guess is only for registers with no
 * link at all. Registers can be renamed, so the name is never the primary key.
 */
export const registersForUnits = <T extends { registerName?: string | null; deviceUnitId?: string | null }>(
  terminals: T[] | null | undefined,
  units: DeviceUnitRow[],
): T[] => (terminals || []).filter((t) => units.some((u) => (
  t.deviceUnitId
    ? t.deviceUnitId === u.unitId
    : String(t.registerName || '').includes(label(u))
)));

/* ═══════════════════════════════════════════════════════════════════════════
   REGISTER EMV — card devices into the location's pool, one TPN each
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * WHAT A REGISTRATION ACTUALLY NEEDS, per device class.
 *
 * This panel used to ask for the TPN and nothing else, which would have been
 * rejected: a SPIn USB device needs its Register Id and Auth Key too, and a
 * cloud processor needs iPOSpays credentials instead of a serial. The fields
 * are DERIVED from the model's class rather than listed once and hoped over,
 * so the form asks for exactly what the door it is knocking on requires.
 *
 * Mirrors RegisterPoiModal's own rules — the same `canSave` conditions, on
 * purpose. Two forms that disagree about what is mandatory is how a device
 * gets registered here and refused there.
 */
type FieldKey =
  | 'serialNumber' | 'spinRegisterId' | 'spinAuthKey' | 'spinTpn'
  | 'registrationCode' | 'iposAuthToken' | 'iposApiKey' | 'iposSecretKey'
  | 'rtpayPin';

export interface FieldSpec {
  key: FieldKey;
  label: string;
  hint: string;
  placeholder: string;
  required: boolean;
  valid?: (v: string) => boolean;
}

/** A field's own caption typed into it — never a credential. Mirrors the
 *  service's `looksLikeLabel` in `emv-device-register.ts`. */
const LOOKS_LIKE_LABEL = /^(spin|ipos)?[\s_-]*(register\s*id|auth\s*key|tpn)$/i;

/**
 * A JWT — which in this form means the OTHER credential.
 *
 * iPOSpays issues two things an operator can fairly call an "auth key". The
 * SPIn Auth Key is a short opaque string (the spec types it String(25)) from
 * S.T.E.A.M → Edit Parameters → Integration → SPIn. The per-TPN "Auth Token"
 * under Settings → Merchant Keys/Ecom Token is a ~300-character JWT, and it is
 * the security key for the online card form — a terminal will never take it.
 * Mirrors `looksLikeJwt` in `emv-device-register.ts`.
 */
const LOOKS_LIKE_JWT = (v: string) => /^[\w-]+\.[\w-]+\.[\w-]+$/.test(v.trim()) && v.trim().length > 60;

const F: Record<FieldKey, FieldSpec> = {
  serialNumber: {
    key: 'serialNumber', label: 'Serial number', required: true,
    placeholder: 'e.g. PAY-001 or sticker serial',
    hint: 'Off the device sticker or box. NMI’s own reported serial is captured separately.',
  },
  /* Both come off ONE portal screen, so both say where — an operator who has
     to go looking twice tends to paste the caption into the box, which is
     exactly how a device in dev ended up carrying "SPIN Register ID" as its
     Register Id AND as its Auth Key. `valid` refuses that spelling outright. */
  spinRegisterId: {
    key: 'spinRegisterId', label: 'SPIn Register Id', required: true,
    placeholder: 'e.g. 9341901',
    hint: 'iPOSpays → S.T.E.A.M → Edit Parameters → pick the TPN → Integration → SPIn. '
      + 'Needed on USB as well as Cloud: the till decides whether a reader exists from this field alone, '
      + 'so a device saved without one is invisible to the app.',
    valid: (v) => !!v.trim() && !LOOKS_LIKE_LABEL.test(v.trim()),
  },
  spinAuthKey: {
    key: 'spinAuthKey', label: 'SPIn Auth Key', required: true,
    placeholder: 'e.g. XQtL9Hxfjh',
    hint: 'Same screen as the Register Id, and a different value from it. A short string — NOT the long '
      + 'Auth Token under Settings → Merchant Keys, which is for the online card form.',
    valid: (v) => !!v.trim() && !LOOKS_LIKE_LABEL.test(v.trim()) && !LOOKS_LIKE_JWT(v),
  },
  spinTpn: {
    key: 'spinTpn', label: 'iPOS Transact TPN', required: true,
    placeholder: '12-digit TPN, e.g. 220926369218',
    hint: 'The 12-digit merchant id. Required for REFUNDS — a SPIn USB terminal cannot do a linked refund, so refunds go through the iPOS Transact cloud keyed to this TPN.',
    valid: (v) => /^\d{12}$/.test(v.trim()),
  },
  registrationCode: {
    key: 'registrationCode', label: 'Registration code', required: true,
    placeholder: 'e.g. A1B2C3D4',
    hint: 'The pairing code shown on the reader.',
    valid: (v) => /^[A-Z0-9]{4,12}$/.test(v.trim().toUpperCase()),
  },
  iposAuthToken: {
    key: 'iposAuthToken', label: 'iPOS Auth Token', required: true,
    placeholder: 'from the iPOSpays portal',
    hint: 'Authorises this processor against the iPOSpays cloud API.',
  },
  /*
    THE KEY PAIR IS OPTIONAL BECAUSE MOST MERCHANTS DO NOT HAVE ONE.

    Refunds and batch close authenticate with the PLATFORM's iPOS Transact key
    pair (SSM) and this device's own TPN. That is right while every merchant
    sits under one Dejavoo partner account. A merchant on their own iPOSpays
    account needs their own pair, and these are it — leave both blank and the
    platform's are used, which is what every device registered so far does.

    Both or neither: half a pair mixed with the platform's other half
    authenticates as nobody, so the form refuses one without the other.
  */
  iposApiKey: {
    key: 'iposApiKey', label: 'iPOS API Key', required: false,
    placeholder: 'dakey_… — only if this merchant has their own',
    hint: 'iPOSpays → Settings → Merchant Keys/Ecom Token → API Key. Leave blank to use the platform’s.',
  },
  iposSecretKey: {
    key: 'iposSecretKey', label: 'iPOS Secret Key', required: false,
    placeholder: 'only if an API Key is set',
    hint: 'Same screen as the API Key. Stored encrypted; never read back.',
  },
  /* RubyTill Pay's staff-menu PIN on the terminal. Sent as `rtpayPin`; the
     service stores only a salted hash. */
  rtpayPin: {
    key: 'rtpayPin', label: 'Terminal PIN', required: true,
    placeholder: '4–6 digits',
    hint: 'For the P17 staff menu. Stored hashed; never shown again.',
    valid: (v) => isValidRtpayPin(v),
  },
};

/** The TPN as RubyTill Pay uses it — the same field, a different job. */
const RTPAY_TPN: FieldSpec = {
  ...F.spinTpn,
  hint: '12 digits. Sent to the terminal at pairing — it is what the terminal charges against.',
};

/** Which fields a class needs, and how it is registered. */
export const CLASS_FORM: Record<string, { fields: FieldSpec[]; blurb: string; cloud?: boolean; rtpay?: boolean }> = {
  /*
    USB ASKS FOR NO REGISTER ID AND NO AUTH KEY, because SPIn USB mode issues
    neither. Those two authorize a host system to reach a terminal through
    Dejavoo's cloud; a till wired to the terminal has no cloud in between to
    authorize against, and the Android plugin omits both tags when they are
    empty. Asking for them here produced two boxes an operator could only fill
    with the wrong thing — which is exactly what happened: the online card
    form's Auth Token went into the Auth Key box.

    The TPN is still asked for, and not for the sale: it is the merchantId iPOS
    Transact refunds against. A SPIn USB terminal cannot do a linked refund
    itself, so a device with no TPN is a device whose sales can never be
    refunded.
  */
  /*
    🔴 THE REGISTER ID IS ASKED FOR HERE, and it did not used to be.

    This lane was built on the belief that "USB mode issues no Register Id".
    That is wrong: a P17 wired over USB in production was registered without
    one, and the till could not see the reader at all — no payment icon, no
    terminal chip, nothing to plug in. `dejavooConfigured()` decides whether a
    register HAS a Dejavoo from exactly one field:

        cachedConfigured = !!cfg?.registerId;

    So a device saved without it is invisible to the app no matter how
    complete the rest of the record looks. Filling the field in afterwards is
    what fixed it. The form asked for everything except the one value that
    determines whether the reader exists.

    Required on BOTH lanes now, for the same reason: SPIn addresses the device
    by Register Id whether it is reached over a cable or over the cloud.
  */
  dejavoo_spin_usb: {
    fields: [F.serialNumber, F.spinRegisterId, F.spinTpn, F.iposApiKey, F.iposSecretKey],
    blurb: 'Wired to the till over USB through a P1/P18 base. In iPOSpays set the TPN to SPIn Mode = USB '
      + '(S.T.E.A.M → Edit Parameters → Integration → SPIn), then copy its Register Id here — the till '
      + 'cannot see the reader without one. The TPN is what refunds are credited through.',
  },
  /*
    THE SAME FIELDS AS THE USB LANE, because it is the same terminal.

    SPIn addresses the device by Register Id with the Auth Key authorizing,
    over a wire or over the cloud alike — the credentials are about SPIn, not
    about the cable. Asking for a different set here would imply a second
    enrolment on the portal that does not exist.
  */
  dejavoo_spin_cloud: {
    fields: [F.serialNumber, F.spinRegisterId, F.spinAuthKey, F.spinTpn, F.iposApiKey, F.iposSecretKey],
    blurb: 'The same terminal, reached over Dejavoo’s SPIn cloud instead of a cable. In iPOSpays set the TPN to '
      + 'SPIn Mode = Cloud (S.T.E.A.M → Edit Parameters → Integration → SPIn), then copy its Register Id and Auth Key here.',
  },
  /*
    RUBYTILL PAY — the same terminal running OUR app. No SPIn credentials at
    all: the terminal pairs itself with a 6-digit code from Super Admin and
    the TPN reaches it in its config. Mode (charge only / customer display)
    is picked with cards beside these fields, not typed.
  */
  [RTPAY_CLASS]: {
    fields: [F.serialNumber, RTPAY_TPN, F.rtpayPin],
    blurb: 'Our own payment app on the terminal — our screens, tips and receipts. After registering, type the pairing code shown here into RubyTill Pay on the terminal.',
    rtpay: true,
  },
  dejavoo_cloud: {
    fields: [F.spinTpn, F.iposAuthToken, F.iposApiKey, F.iposSecretKey],
    blurb: 'A Dejavoo Cloud (iPOSpays) processor — card-not-present. It has no serial to read: the TPN and its token are the whole identity.',
    cloud: true,
  },
  nmi_direct_vp3350: {
    fields: [F.serialNumber],
    blurb: 'Pairs over the NMI SDK. No registration code — the serial is enough.',
  },
  cloud: {
    fields: [F.serialNumber, F.registrationCode],
    blurb: 'NMI Cloud Lane. Pairs with the code shown on the reader.',
  },
};

/**
 * ONE REGISTERED VALUE, with its absence said out loud.
 *
 * A field left blank and a field nobody thought to show look the same when the
 * row just omits it — and for a SPIn credential that difference is the whole
 * point, because a device missing its Auth Key looks perfectly registered right
 * up until the first card. So a missing value prints "not set" in amber.
 */
const Fact: React.FC<{ label: string; value?: string | null; mono?: boolean }> = ({
  label, value, mono = true,
}) => (
  <div className="min-w-0">
    <dt className="text-[10px] uppercase tracking-[0.06em] text-stone-400">{label}</dt>
    <dd className={value
      ? `truncate text-[11.5px] text-stone-800 ${mono ? 'font-mono' : ''}`
      : 'text-[11.5px] font-semibold text-amber-700'}
    >
      {value || 'not set'}
    </dd>
  </div>
);

/** The last number used at this location, so the next name continues the run. */
const lastSeq = (pool: any[]): number => {
  let max = 0;
  for (const d of pool || []) {
    const m = /(\d{1,4})\s*$/.exec(String(d?.deviceName || ''));
    if (m) max = Math.max(max, parseInt(m[1], 10));
  }
  return max;
};

export const RegisterEmvPanel: React.FC<LaneProps & {
  varSheets: VarSheet[];
  /**
   * WHY PAIRING IS HELD, if it is — the words shown on the row and the reason
   * the button is dead. Set when "Setup configurations" or "Download
   * parameters" has not been ticked: a terminal with no parameters downloaded
   * pairs into a register perfectly well and then fails at the first card,
   * which looks like a pairing fault and is not.
   */
  blockedReason?: string;
}> = ({
  tenantId, tenantName, locationId, locationName, units, models, varSheets, onChanged, blockedReason,
}) => {
  const [pool, setPool] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dataVersion = useDataVersion();
  const [busy, setBusy] = useState<string | null>(null);
  const [form, setForm] = useState<Record<string, Record<string, string>>>({});
  /* A CUSTOM confirm, never `window.confirm` — platform rule, and the native
     one is invisible inside the iOS webview anyway. */
  const [confirmRemove, setConfirmRemove] = useState<
    { deviceId: string; name: string; attachedTo?: string } | null
  >(null);
  /*
    EDITING THE SPIn PAIR ON A DEVICE THAT ALREADY EXISTS.

    Registration is not an edit — it mints a new deviceId and resets the
    binding — so a device created by the order's "Set up POS" runner, which
    never asks for these, had no way to acquire them. Keyed by deviceId so two
    readers on one order are edited independently.
  */
  /* RubyTill Pay: the code issued right after registering, keyed by the new
     deviceId, so the pool row below shows it the moment the device lands. */
  const [rtpayCodes, setRtpayCodes] = useState<Record<string, RtpayPairingCode>>({});
  const [spinEdit, setSpinEdit] = useState<
    {
      deviceId: string; isCloud: boolean;
      serialNumber: string; spinRegisterId: string; spinAuthKey: string;
      spinTpn: string; iposApiKey: string; iposSecretKey: string;
      error?: string;
    } | null
  >(null);

  const saveSpinCredentials = async () => {
    if (!spinEdit || !locationId) return;
    setBusy(`${spinEdit.deviceId}:spin`);
    try {
      /*
        SECRETS ARE SENT ONLY WHEN RETYPED. We are never given the stored
        value — only a mask — so an empty secret box means "leave it alone",
        never "clear it". The plain fields go every time: they are prefilled
        with what is stored, so sending them back unchanged is a no-op.
      */
      await api.posNmi.updateEmvDevice(tenantId, locationId, spinEdit.deviceId, {
        deviceConfig: {
          serialNumber: spinEdit.serialNumber.trim(),
          spinRegisterId: spinEdit.spinRegisterId.trim(),
          spinTpn: spinEdit.spinTpn.trim(),
          ...(spinEdit.isCloud && spinEdit.spinAuthKey.trim()
            ? { spinAuthKey: spinEdit.spinAuthKey.trim() } : {}),
          ...(spinEdit.iposApiKey.trim() ? { iposApiKey: spinEdit.iposApiKey.trim() } : {}),
          ...(spinEdit.iposSecretKey.trim() ? { iposSecretKey: spinEdit.iposSecretKey.trim() } : {}),
        },
      });
      setSpinEdit(null);
      onChanged();
      toastService.success('Device details saved.');
    } catch (e: any) {
      /* The service says WHICH field is wrong and where to read the right
         value from — pass its sentence through rather than "failed". */
      setSpinEdit((s) => (s ? { ...s, error: e?.serverMessage || 'Could not save those details.' } : s));
    } finally {
      setBusy(null);
    }
  };

  /*
    REMOVING A DEVICE THAT IS STILL ON A TILL.

    The service refuses (DEVICE_IN_USE) rather than silently detaching, and
    this screen has no unassign of its own — so the refusal used to be a dead
    end: the only release lived on the terminal's Payment Types tab, which is
    not where an operator who attached the wrong reader is standing.

    So the refusal is now the FIRST half of the confirm. It comes back naming
    the terminal, the dialog says so, and the second press detaches for real —
    binding cleared, terminal's card lane stripped, reader deregistered. Which
    is also why `force` is never sent on the first attempt: the operator has to
    have seen the terminal's name before it is taken off a live till.
  */
  const removeFromPool = async () => {
    if (!confirmRemove || !locationId) return;
    setBusy(confirmRemove.deviceId);
    try {
      await api.posNmi.deregisterEmvDevice(
        tenantId, locationId, confirmRemove.deviceId, !!confirmRemove.attachedTo,
      );
      toastService.success(
        confirmRemove.attachedTo
          ? `${confirmRemove.name} detached and removed. That terminal is back to no card device.`
          : `${confirmRemove.name} removed from this location's pool.`,
      );
      setConfirmRemove(null);
      await load();
      onChanged();
    } catch (e: any) {
      const code = e?.code || e?.error;
      if (code === 'DEVICE_IN_USE') {
        setConfirmRemove({
          ...confirmRemove,
          attachedTo: e?.currentRegisterId || 'a terminal',
        });
      } else {
        // `message` is the raw code; `serverMessage` is the sentence written
        // for a human. Prefer the sentence, fall back to ours — never the code.
        toastService.error(e?.serverMessage || 'Could not remove this device.');
      }
    } finally {
      setBusy(null);
    }
  };

  const load = useCallback(async () => {
    if (!locationId) { setPool([]); return; }
    setError(null);
    try {
      const res: any = await api.posNmi.listEmvDevicesAtLocation(tenantId, locationId);
      setPool(res?.devices || res?.items || (Array.isArray(res) ? res : []));
    } catch (e: any) {
      setError(e?.message || 'Could not read this location’s device pool.');
    }
  }, [tenantId, locationId]);

  // dataVersion: re-read after any save on the order page
  useEffect(() => { load(); }, [load, dataVersion]);

  /* A device is "in the pool" when a pool entry carries its serial. The pool
     is keyed by its own deviceId, so the serial is the only thing the two
     records share — matching on anything else would claim a device is
     registered because some OTHER reader at the location is. */
  const registeredSerials = useMemo(
    () => new Set((pool || []).map((d) => String(d.serialNumber || '').trim()).filter(Boolean)),
    [pool],
  );

  const unregistered = units.filter((u) => !(u.serialNumber && registeredSerials.has(u.serialNumber.trim())));

  /**
   * THE NAME, BUILT FROM WHERE IT LIVES.
   *
   * `Foggy Frog Vapes - Colorado Springs - 003`. A pool of "Front Counter
   * Reader" and "Reader 2" across four shops cannot be read at all; leading
   * with the tenant and the store means the list sorts into branches by
   * itself, and the trailing number says how many that branch has.
   *
   * Continues the run already at this location rather than restarting, and
   * stays editable — it is a suggestion, not a rule.
   */
  const suggestedName = (index: number) => {
    const seq = String(lastSeq(pool || []) + index + 1).padStart(3, '0');
    return [tenantName, locationName || locationId, seq].filter(Boolean).join(' - ');
  };

  const valueOf = (unitId: string, key: string, fallback = '') =>
    form[unitId]?.[key] ?? fallback;
  const setValue = (unitId: string, key: string, v: string) =>
    setForm((p) => ({ ...p, [unitId]: { ...(p[unitId] || {}), [key]: v } }));

  /**
   * THE LANE THIS UNIT IS BEING REGISTERED ON.
   *
   * `resolvePaymentConfig` gives the model's DEFAULT — never
   * `getModelDef(modelName)` on its own, which is an exact name match, so a
   * catalog row called "Dejavoo P17 Android Pin Pad" resolved to nothing and
   * the service defaulted it to NMI cloud.
   *
   * But a model may offer more than one lane, and then the operator's pick
   * wins: a P17 in front of them is either wired to the till or reached over
   * SPIn's cloud, and only they know which. Everything downstream — the blurb,
   * the fields, the chip, the POST — must read the CHOSEN id, or picking cloud
   * still asks for the USB lane's fields.
   */
  const laneFor = (u: DeviceUnitRow, model: any) => {
    const fallback = resolvePaymentConfig(model as any);
    /*
      DEGRADE TO ONE LANE, never to a blank screen.

      `paymentConfigOptionsFor` is a RUNTIME value from shared-types, and Vite
      pre-bundles that package. A dev server started before the function existed
      serves an optimised copy without it and this threw `is not a function`,
      which took down the whole order page rather than one control — the same
      trap `osInstallGate` carries a guard for in UnitProvisioning.tsx.
      (Deleting node_modules/.vite under a RUNNING server does not fix it: Vite
      keeps the bundle in memory and rewrites the directory. Restart it.)

      Falling back to the resolved config alone is the honest degradation: the
      picker disappears and registration behaves exactly as it did before
      multiple lanes existed.
    */
    const options = typeof paymentConfigOptionsFor === 'function'
      ? paymentConfigOptionsFor(model as any)
      : (fallback ? [fallback] : []);
    const id = valueOf(u.unitId, 'paymentConfigId', fallback?.id || '') || fallback?.id || '';
    /* A stale pick — the model's option list changed under an open form —
       falls back to the default rather than registering a lane the model no
       longer offers. */
    const chosen = options.some((o) => o.id === id)
      ? getPaymentConfig(id)
      : fallback;
    return { options, cfg: chosen || fallback, chosenId: chosen?.id || fallback?.id || '' };
  };

  const register = async (u: DeviceUnitRow, index: number) => {
    const model = modelOf(models, u.modelId);
    const { cfg, chosenId } = laneFor(u, model);
    const shape = CLASS_FORM[chosenId] || CLASS_FORM.cloud;

    const name = valueOf(u.unitId, 'deviceName', suggestedName(index)).trim();
    if (!name) { toastService.warning('Give the device a name.'); return; }

    const values: Record<string, string> = {};
    for (const f of shape.fields) {
      const raw = valueOf(u.unitId, f.key, f.key === 'serialNumber' ? (u.serialNumber || '') : '').trim();
      if (f.required && !raw) { toastService.warning(`${f.label} is required.`); return; }
      if (raw && f.valid && !f.valid(raw)) { toastService.warning(`${f.label} does not look right.`); return; }
      if (raw) values[f.key] = raw;
    }

    /* Both halves of the iPOS pair or neither — one half mixed with the
       platform's other half authenticates as nobody, and the failure would not
       surface until somebody tried a refund weeks later. */
    if (!!values.iposApiKey !== !!values.iposSecretKey) {
      toastService.warning('Enter both the iPOS API Key and Secret Key, or leave both blank to use the platform’s.');
      return;
    }
    /* Two identical values mean one box was filled and copied into the other. */
    if (values.spinRegisterId && values.spinRegisterId === values.spinAuthKey) {
      toastService.warning('The SPIn Register Id and Auth Key are different values — check the portal.');
      return;
    }

    setBusy(u.unitId);
    try {
      if (shape.cloud) {
        await api.posNmi.registerCloudProcessor(tenantId, locationId!, {
          deviceName: name,
          spinTpn: values.spinTpn,
          iposAuthToken: values.iposAuthToken,
          iposApiKey: values.iposApiKey,
          iposSecretKey: values.iposSecretKey,
        });
      } else {
        const resp: any = await api.posNmi.registerEmvDeviceToLocation(tenantId, locationId!, {
          deviceName: name,
          model: model?.modelName,
          // The CHOSEN lane, not the model's default — this id becomes the
          // stored `deviceClass` and the reporting key for every sale on it.
          paymentConfigId: chosenId,
          connectionType: cfg?.profile?.connectionType,
          ...values,
          ...(shape.rtpay ? { rtpayMode: (valueOf(u.unitId, 'rtpayMode', 'charge_only') as RtpayRegisterMode) } : {}),
        } as any);
        /* RubyTill Pay pairs from the terminal side: issue the first code now
           so the operator can type it straight away. A failure here is not a
           failed registration — the row offers "Get pairing code". */
        const newId = resp?.device?.deviceId || resp?.deviceId;
        if (shape.rtpay && newId) {
          try {
            const pc = await api.posNmi.newRtpayPairingCode(tenantId, newId);
            if (pc?.code) setRtpayCodes((p) => ({ ...p, [newId]: { code: String(pc.code), expiresAt: pc.expiresAt } }));
          } catch { /* the pool row offers a fresh code */ }
        }
      }
      toastService.success(`${name} registered to this location.`);
      setForm((p) => ({ ...p, [u.unitId]: {} }));
      await load();
      onChanged();
    } catch (e: any) {
      /* The service's own refusals, said in its words rather than "failed". */
      const code = e?.error || e?.code;
      toastService.error(
        code === 'GATEWAY_NOT_CONFIGURED'
          ? 'Save this location’s gateway credentials before registering a device.'
          : code === 'NMI_ID_IN_USE'
            ? 'That identifier is already registered to another device.'
            // The service names the offending fields; its sentence already says
            // where in the portal to read them, so pass it through verbatim.
            : e?.serverMessage || 'Could not register this device.',
      );
    } finally {
      setBusy(null);
    }
  };

  if (!locationId) return <Problem text="This order has no location, so there is no device pool to register into." />;
  // A failed background re-read keeps the last good copy on screen; the
  // error box is only for a panel that has never loaded.
  if (error && pool === null) return <Problem text={error} onRetry={load} />;
  if (pool === null) return <Busy />;
  if (units.length === 0) return <p className="text-[12.5px] text-stone-500">No card devices allocated to this order yet.</p>;

  return (
    <div className="flex flex-col gap-2">
      <ForLocation tenantName={tenantName} locationName={locationName} locationId={locationId} what="Device pools" />

      {/* Above the readers, because it governs all of them — and because it has
          to be done BEFORE a register can be switched to surcharge. */}
      <SurchargeApprovalSection
        tenantId={tenantId}
        locationId={locationId}
        locationName={locationName}
        onChanged={onChanged}
      />

      {units.map((u) => {
        const model = modelOf(models, u.modelId);
        const { options: laneOptions, cfg, chosenId } = laneFor(u, model);
        const shape = chosenId ? CLASS_FORM[chosenId] : undefined;
        const inPool = !!u.serialNumber && registeredSerials.has(u.serialNumber.trim());
        const index = unregistered.findIndex((x) => x.unitId === u.unitId);
        /*
          THE SHEET FOR THIS READER, not for the application.
          One sheet anywhere used to enable every row, which is how a reader
          gets keyed against a TID nobody issued for it.
        */
        const sheet = activeSheetFor(varSheets, u.unitId);

        if (inPool) {
          /*
            WHAT WAS ACTUALLY REGISTERED, not just that something was.

            "In pool" alone made the operator go to another screen to find out
            which terminal this is and whether its TPN went in — and even there,
            nothing showed the SPIn pair, so the one question worth asking after
            registering ("did the right values go in?") had no answer on any
            screen. That is how a device sat in the pool carrying the string
            "SPIN Register ID" as both its Register Id and its Auth Key.

            Still a confirmation, not a credential viewer: the Register Id is
            shown whole because it ADDRESSES the terminal and is what you
            compare against the portal, while the Auth Key — the half that
            authorizes — and the TPN come back as last-4 only, which confirms a
            paste and is useless to anyone who copies it.
          */
          const dev = (pool || []).find(
            (d) => String(d.serialNumber || '').trim() === (u.serialNumber || '').trim(),
          );
          return (
            <div key={u.unitId} className="rounded-xl border border-stone-200 bg-white p-3">
              <Row model={model} title={model?.modelName || u.modelId} sub={`${label(u)} · ${u.serialNumber}`} done>
                <Chip tone="ok"><Check size={10} /> In pool</Chip>
              </Row>
              {dev && (
                <div className="mt-2 rounded-lg bg-stone-50 px-3 py-2 text-[11.5px] text-stone-600">
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                    <span className="font-semibold text-stone-800">{dev.deviceName}</span>
                    {/*
                      THE LANE THE DEVICE WAS REGISTERED ON, read off the device
                      — not off the model.

                      `cfg` is the MODEL's config, which for a model offering
                      both lanes is only its default. A P17 registered as SPIn
                      Cloud therefore displayed "Dejavoo P Series — USB", which
                      is the one thing this row exists to tell you and the one
                      thing it got wrong. The device's own `deviceClass` is what
                      was actually stored, so that is what is shown; `cfg` stays
                      as the fallback for rows that predate it.
                    */}
                    {isRtpayDevice(dev) && isTapToPayDevice(dev) ? (
                      <span>Android Tap to Pay</span>
                    ) : (getPaymentConfig(dev.deviceClass) || cfg) && (
                      <span>{(getPaymentConfig(dev.deviceClass) || cfg)!.label}</span>
                    )}
                    <span>{dev.registerId ? 'assigned to a terminal' : 'not assigned to a terminal yet'}</span>
                    {/* Beside "Remove from pool", not inside the field list —
                        a button between two facts reads as a third fact.
                        Not on RubyTill Pay: it has no SPIn pair to edit. */}
                    {!isRtpayDevice(dev) && <button
                      onClick={() => setSpinEdit({
                        deviceId: dev.deviceId,
                        isCloud: dev.deviceClass === 'dejavoo_spin_cloud',
                        serialNumber: dev.serialNumber || '',
                        spinRegisterId: dev.spinRegisterId || '',
                        spinTpn: dev.tpn || dev.tpnMasked || dev.spinTpn || '',
                        // Secrets are never prefilled — we only ever hold a mask.
                        spinAuthKey: '',
                        iposApiKey: '',
                        iposSecretKey: '',
                      })}
                      className="ml-auto min-h-[32px] rounded-lg border border-stone-300 px-2.5 text-[11px] font-semibold text-stone-600 hover:border-ruby hover:bg-ruby/5 hover:text-ruby"
                    >
                      Edit details
                    </button>}
                    <button
                      onClick={() => setConfirmRemove({ deviceId: dev.deviceId, name: dev.deviceName })}
                      className={`${isRtpayDevice(dev) ? 'ml-auto ' : ''}min-h-[32px] rounded-lg border border-stone-300 px-2.5 text-[11px] font-semibold text-stone-600 hover:border-red-300 hover:bg-red-50 hover:text-red-700`}
                    >
                      Remove from pool
                    </button>
                  </div>

                  {/* WHAT WENT IN, said field by field. A missing one is called
                      out in amber rather than left blank — an absent Auth Key
                      and an Auth Key nobody looked at read identically when the
                      row simply omits it. */}
                  <dl className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5 border-t border-stone-200 pt-2">
                    <Fact label="Serial" value={dev.serialNumber} />
                    {/*
                      BOTH SPIn LANES HAVE THESE, and hiding them on USB is what
                      sent a terminal to a counter unable to take a card.

                      This block used to be cloud-only, on the belief that SPIn
                      issues the pair under SPIn Mode = Cloud and not under USB.
                      That is wrong: a USB terminal has them too, just further in
                      — S.T.E.A.M → Edit Parameters, not Settings — which is
                      exactly why they get missed. `get-dejavoo-config` refuses to
                      hand the POS any credentials unless BOTH are stored, and the
                      POS then sends a sale with no <RegisterId>/<AuthKey>; the
                      terminal answers "Invalid Request" and never prompts for a
                      card. The amber "not set" removed here was the only warning
                      that said so.
                    */}
                    {(dev.deviceClass === 'dejavoo_spin_cloud' || dev.deviceClass === 'dejavoo_spin_usb') && (
                      <>
                        <Fact label="SPIn Register Id" value={dev.spinRegisterId} />
                        {/* Cloud only: USB is issued a Register Id and no Auth
                            Key, so printing it on USB puts an amber "not set"
                            against a device that is correctly configured. */}
                        {dev.deviceClass === 'dejavoo_spin_cloud' && (
                          <Fact label="SPIn Auth Key" value={dev.spinAuthKeyMasked} />
                        )}
                      </>
                    )}
                    {/* Shown whole: the TPN identifies the merchant's own
                        terminal on their own portal and protects nothing by
                        being hidden. `tpnMasked` is the older field, kept as a
                        fallback for a service not yet returning `tpn`. */}
                    <Fact label="TPN" value={dev.tpn || dev.tpnMasked} />
                    {/* Which pair, not just whether — a merchant on their own
                        iPOSpays account has a key and a secret worth telling
                        apart when a refund is refused. Registered before the
                        last-4 hint existed → "set", never invented digits. */}
                    {dev.hasOwnIposKeys ? (
                      <>
                        <Fact label="iPOS API Key" value={dev.iposApiKeyMasked || 'set'} />
                        <Fact label="iPOS Secret" value={dev.iposSecretKeyMasked || 'set'} />
                      </>
                    ) : (
                      <Fact label="iPOS keys" value="platform default" mono={false} />
                    )}
                  </dl>

                  {/* RubyTill Pay pairs with a code typed on the terminal. */}
                  {isRtpayDevice(dev) && (
                    <div className="mt-2">
                      <RtpayPairingCodePanel
                        tenantId={tenantId}
                        deviceId={dev.deviceId}
                        initial={rtpayCodes[dev.deviceId] || null}
                        onPaired={() => { load(); onChanged(); }}
                      />
                    </div>
                  )}

                  {/*
                    WHERE THE PAIR GETS TYPED IN. Inline under the device it
                    belongs to, because two readers on one order have two
                    different Register Ids and a modal loses which is which.
                  */}
                  {spinEdit?.deviceId === dev.deviceId && (
                    <div className="mt-2 rounded-lg border border-ruby/30 bg-ruby/5 p-3">
                      <p className="text-[11.5px] leading-relaxed text-stone-600">
                        On the terminal this is under{' '}
                        <span className="font-semibold">S.T.E.A.M → Edit Parameters</span> → select the TPN
                        → Integration → SPIn — not under Settings, which is where most people look.
                        {dev.deviceClass === 'dejavoo_spin_cloud'
                          ? <> The Auth <span className="font-semibold">Key</span> is the short value beside
                              the Register Id, not the long Auth Token.</>
                          : <> USB mode issues a Register Id and no Auth Key.</>}
                      </p>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {([
                          ['Serial', 'serialNumber', '', false],
                          ['SPIn Register Id', 'spinRegisterId', 'e.g. 9341901', false],
                          ...(dev.deviceClass === 'dejavoo_spin_cloud'
                            ? [['SPIn Auth Key', 'spinAuthKey',
                                dev.spinAuthKeyMasked ? 'leave blank to keep ' + dev.spinAuthKeyMasked : '', true]]
                            : []),
                          ['TPN', 'spinTpn', '12 digits', false],
                          ['iPOS API Key', 'iposApiKey',
                            dev.iposApiKeyMasked ? 'leave blank to keep ' + dev.iposApiKeyMasked : 'platform default', true],
                          ['iPOS Secret', 'iposSecretKey',
                            dev.iposSecretKeyMasked ? 'leave blank to keep ' + dev.iposSecretKeyMasked : 'platform default', true],
                        ] as [string, keyof typeof spinEdit, string, boolean][]).map(([label, key, placeholder]) => (
                          <label key={key as string} className="block">
                            <span className="text-[11px] font-semibold text-stone-600">{label}</span>
                            <input
                              value={spinEdit[key] as string}
                              onChange={(e) => setSpinEdit({ ...spinEdit, [key]: e.target.value, error: undefined })}
                              className="mt-1 min-h-[44px] w-full rounded-lg border border-stone-300 px-3 font-mono text-[13px] focus:border-ruby focus:outline-none"
                              placeholder={placeholder}
                            />
                          </label>
                        ))}
                      </div>
                      {spinEdit.error && (
                        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[11.5px] leading-relaxed text-amber-800">
                          {spinEdit.error}
                        </p>
                      )}
                      <div className="mt-2 flex gap-2">
                        <button
                          onClick={saveSpinCredentials}
                          disabled={
                            !spinEdit.spinRegisterId.trim()
                            // Only the Register Id gates saving; secrets left blank
                            // simply mean "keep what is stored".
                            
                            || busy === `${dev.deviceId}:spin`
                          }
                          className="min-h-[44px] rounded-lg bg-ruby px-4 text-[12px] font-semibold text-white hover:bg-ruby-deep disabled:opacity-40"
                        >
                          {busy === `${dev.deviceId}:spin` ? 'Saving…' : 'Save credentials'}
                        </button>
                        <button
                          onClick={() => setSpinEdit(null)}
                          className="min-h-[44px] rounded-lg border border-stone-300 px-4 text-[12px] font-semibold text-stone-600 hover:bg-stone-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        }

        /*
          THREE WAYS A READER HAS NO FORM HERE, and they are three different
          sentences. Collapsing them into "unknown device class" is what sent
          an operator to read source code to find out what to do next.
        */
        if (!cfg || cfg.id === 'none' || !cfg.orderUi || !shape) {
          const [chip, note] = !cfg
            ? ['no payment config',
               'This device has never been given a payment config. Set one on it in Device Management and it will register from here.']
            : cfg.id === 'none'
              ? ['no payment config',
                 'This device is checked in with no payment lane chosen. Set its config in Device Management before registering it.']
              : /* NMI. Working exactly as it always has — just not from the order
                   rail, which would be a second registration form for a lane
                   that already has one. */
                [`${cfg.label} — legacy`,
                 `${cfg.label} devices are registered from the POS terminal screen, not from an order. Nothing about them has changed.`];
          return (
            <div key={u.unitId} className="rounded-xl border border-stone-200 bg-white p-3">
              <Row model={model} title={model?.modelName || u.modelId} sub={label(u)}>
                <Chip tone="bad">{chip}</Chip>
              </Row>
              <p className="mt-2 rounded-lg bg-stone-50 px-3 py-2 text-[11.5px] leading-relaxed text-stone-600">
                {note}
              </p>
            </div>
          );
        }

        return (
          <div key={u.unitId} className="rounded-xl border border-stone-200 bg-white p-3">
            <Row model={model} title={model?.modelName || u.modelId} sub={`${label(u)} · ${cfg.label}`} done>
              <Chip>{cfg.profile?.connectionType || 'cloud'}</Chip>
            </Row>

            {laneOptions.length > 1 && (
              /*
                SHOWN ONLY WHERE THERE IS A REAL CHOICE.

                A one-option control teaches nothing and can still be got wrong,
                so every catalog row that offers a single config renders no
                control at all — exactly as it did before this existed.

                Changing the pick changes the blurb and the field list below,
                because the shape is read from this value rather than from the
                model's default.
              */
              <div className="mt-2 flex flex-col gap-1">
                <span className="text-[11px] font-semibold text-stone-600">How it connects</span>
                {/* SearchableDropdown, never a native select — platform rule. */}
                <div className="w-full sm:max-w-sm">
                  <SearchableDropdown
                    ariaLabel="How it connects"
                    options={laneOptions.map((o) => ({ value: o.id, label: o.label }))}
                    value={chosenId}
                    onChange={(v) => { if (v) setValue(u.unitId, 'paymentConfigId', v); }}
                    searchPlaceholder="Search connection types…"
                  />
                </div>
                <span className="text-[10.5px] leading-relaxed text-stone-400">{cfg?.hint}</span>
              </div>
            )}

            <p className="mt-2 rounded-lg bg-stone-50 px-3 py-2 text-[11.5px] leading-relaxed text-stone-600">
              {shape.blurb}
            </p>

            <div className="mt-2 grid gap-2 sm:grid-cols-2">
              <label className="block">
                <span className="block text-[11px] font-semibold text-stone-600">Device name</span>
                <input
                  value={valueOf(u.unitId, 'deviceName', suggestedName(Math.max(0, index)))}
                  onChange={(e) => setValue(u.unitId, 'deviceName', e.target.value)}
                  className="mt-1 min-h-[38px] w-full rounded-lg border border-stone-300 px-2 text-[12.5px]"
                />
                <span className="mt-0.5 block text-[10.5px] text-stone-400">
                  Tenant · store · number, so a multi-shop pool sorts into branches by itself.
                </span>
              </label>

              {shape.fields.map((f) => {
                const v = valueOf(u.unitId, f.key, f.key === 'serialNumber' ? (u.serialNumber || '') : '');
                const bad = !!v.trim() && f.valid && !f.valid(v);
                return (
                  <label key={f.key} className="block">
                    <span className="block text-[11px] font-semibold text-stone-600">
                      {f.label}{!f.required && <span className="font-normal text-stone-400"> · optional</span>}
                    </span>
                    <input
                      value={v}
                      onChange={(e) => setValue(u.unitId, f.key, e.target.value)}
                      placeholder={f.placeholder}
                      inputMode={f.key === 'spinTpn' || f.key === 'rtpayPin' ? 'numeric' : undefined}
                      type={f.key === 'rtpayPin' ? 'password' : undefined}
                      autoComplete={f.key === 'rtpayPin' ? 'new-password' : undefined}
                      className={`mt-1 min-h-[38px] w-full rounded-lg border px-2 font-mono text-[12.5px] ${
                        bad ? 'border-red-300 bg-red-50' : 'border-stone-300'}`}
                    />
                    <span className="mt-0.5 block text-[10.5px] leading-relaxed text-stone-400">{f.hint}</span>
                  </label>
                );
              })}
            </div>

            {shape.rtpay && (
              <div className="mt-2">
                <span className="mb-1 block text-[11px] font-semibold text-stone-600">What it does at this register</span>
                <RtpayModeCards
                  value={valueOf(u.unitId, 'rtpayMode', 'charge_only') as RtpayRegisterMode}
                  onChange={(m) => setValue(u.unitId, 'rtpayMode', m)}
                />
              </div>
            )}

            {!sheet && (
              <p className="text-[11.5px] text-amber-900 mt-1">
                No sheet bound to this reader. Registering against a TID nobody was issued is how a reader ends up keyed to the wrong merchant.
              </p>
            )}
            {sheet && (
              <p className="text-[11.5px] text-stone-500 mt-1">
                Sheet bound: <b className="text-stone-700">{sheet.fileName || sheet.varId}</b>
              </p>
            )}

            {blockedReason && (
              <p className="mt-2 rounded-lg border-l-[3px] border-amber-400 bg-amber-50 px-3 py-2 text-[11.5px] leading-relaxed text-amber-900">
                {blockedReason}
              </p>
            )}

            <div className="mt-2 flex justify-end">
              <Action
                onClick={() => register(u, Math.max(0, index))}
                busy={busy === u.unitId}
                disabled={!sheet || !!blockedReason}
                title={blockedReason || (sheet ? undefined : 'Bind a VAR sheet to this reader first.')}
              >
                <Cpu size={12} /> Register &amp; pair
              </Action>
            </div>
          </div>
        );
      })}

      {confirmRemove && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setConfirmRemove(null)}
        >
          <div
            className="w-full max-w-[480px] overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-stone-200 px-5 py-4">
              <h3 className="text-[15px] font-bold text-stone-900">
                {confirmRemove.attachedTo
                  ? 'Detach this device from its terminal and remove it?'
                  : 'Remove this device from the pool?'}
              </h3>
              <p className="mt-1 text-[12.5px] text-stone-500">{confirmRemove.name}</p>
            </div>
            <div className="px-5 py-4 text-[12.5px] leading-relaxed text-stone-600">
              {confirmRemove.attachedTo && (
                /* The terminal is named because this is the press that takes a
                   reader off a till that may be mid-shift. */
                <div className="mb-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-amber-900">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    Still attached to{' '}
                    <span className="font-mono text-[11.5px] font-semibold">{confirmRemove.attachedTo}</span>.
                    Detaching turns that terminal's card lane off and clears its reader, so it
                    goes back to taking no card payments until a device is set up again.
                  </span>
                </div>
              )}
              {/* Said plainly, because the SPIn keys are write-only: nobody can
                  read them back off the record to retype them. */}
              It leaves this location's device pool and its SPIn credentials go with it —
              the Register Id, Auth Key and TPN are write-only, so registering it again
              means reading all three off the iPOSpays portal a second time.
              <span className="mt-2 block text-stone-500">The physical device and the order are untouched.</span>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3.5">
              <button
                onClick={() => setConfirmRemove(null)}
                className="min-h-[44px] rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={removeFromPool}
                disabled={busy === confirmRemove.deviceId}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busy === confirmRemove.deviceId && <RubyGemMark size={13} drawing color="currentColor" />}
                {confirmRemove.attachedTo ? 'Detach and remove' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   CREATE THE REGISTER — one per POS device, with its provisioning code
   ═══════════════════════════════════════════════════════════════════════════ */

export const CreateRegisterPanel: React.FC<LaneProps> = ({
  stageIds,
  tenantId, tenantName, locationId, locationName, units, models, onChanged,
}) => {
  const dataVersion = useDataVersion();
  const [terminals, setTerminals] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [name, setName] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState<string | null>(null);
  /* Which device's quick view is open. Held by id rather than by object so the
     popup follows the unit when the lane reloads under it. */
  const [peek, setPeek] = useState<string | null>(null);
  /** unitId -> 'saving' | 'saved', for the line under the Display name box. */
  const [nameSave, setNameSave] = useState<Record<string, 'saving' | 'saved'>>({});
  const saveTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  /*
    THE NAME IS SAVED AS IT IS TYPED, not when the register is created.

    It used to live only in React state, so a refresh — or a tab change, or a
    dropped connection — threw it away silently and handed the next person the
    model name back. It is written to the UNIT as a draft default, debounced so
    a name is one write rather than one per keystroke.

    Failure is deliberately SILENT here: the value is still in the box and
    still used when the register is created, so a toast per keystroke-burst
    would be noise about something that has not actually been lost yet.
  */
  const saveName = useCallback((unitId: string, value: string) => {
    clearTimeout(saveTimers.current[unitId]);
    setNameSave((p) => ({ ...p, [unitId]: 'saving' }));
    saveTimers.current[unitId] = setTimeout(async () => {
      try {
        await hardwareApi.upsertUnit({ unitId, registerName: value.trim() || undefined });
        setNameSave((p) => ({ ...p, [unitId]: 'saved' }));
      } catch {
        setNameSave((p) => {
          const next = { ...p };
          delete next[unitId];
          return next;
        });
      }
    }, 700);
  }, []);

  /* A pending write must not outlive the panel — it would set state on an
     unmounted component and, worse, fire after the operator navigated away
     from the decision. */
  useEffect(() => () => {
    Object.values(saveTimers.current).forEach(clearTimeout);
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const res: any = await api.posTerminals.list(tenantId);
      setTerminals(res?.terminals || []);
    } catch (e: any) {
      setError(e?.message || 'Could not read this tenant’s registers.');
    }
  }, [tenantId]);

  // dataVersion: re-read after any save on the order page
  useEffect(() => { load(); }, [load, dataVersion]);

  const registerFor = useCallback(
    (u: DeviceUnitRow) => registerForUnit(terminals, u),
    [terminals],
  );

  const create = async (u: DeviceUnitRow) => {
    const model = modelOf(models, u.modelId);
    /* What is in the box, whatever put it there: this session's typing, the
       draft saved on the unit by an earlier one, or the default. The three are
       resolved in the same order the input renders them, so the register is
       always named what the operator was looking at. */
    const chosen = (name[u.unitId] ?? u.registerName ?? '').trim()
      || `${model?.modelName || 'Register'} · ${label(u)}`;
    /*
      The name used to be FORCED to contain the tracking id, and the refusal
      said why: it was how the register was matched back to the device. It no
      longer is — `deviceUnitId` is sent below and is now projected by
      `pos-terminals-list`, so the link survives any name. Demanding "RTA-000021"
      inside a register the shop will read as "Front counter" was a naming rule
      imposed by a lookup that has been replaced.
    */
    if (!chosen) {
      toastService.warning('Give the register a name.');
      return;
    }
    setBusy(u.unitId);
    try {
      const resp: any = await api.posTerminals.provision({
        tenantId,
        locationId: locationId!,
        /* Never the TENANT's name as a fallback — a register labelled with the
           business rather than the branch is unreadable at a four-shop tenant.
           The id is ugly and correct; the name is only used when it is real. */
        locationName: locationName || locationId,
        registerName: chosen,
        /*
          THE SCREEN TYPE COMES FROM THE CATALOG, NOT FROM A CONSTANT.

          This used to read `model?.category === 'pos_terminal' ? 'pos' : 'pos'`
          — both arms 'pos' — so every register this panel created was a plain
          terminal. A K10 whose catalog row says `android_dual_screen` then
          activated with `terminalType: 'pos'`, and the device, which matches
          that string exactly to decide whether it has a second screen, came up
          single-screen and on the lane payment provider. The catalog answer was
          right the whole way; this line threw it away.
        */
        terminalType: provisionTypeForModel(model),
        /* The link back to the catalog. The provisioning handler has always
           accepted these and written them when present; nothing sent them, so
           every PROVISION# row carries a blank deviceModelId and a register
           cannot be traced to the box it was minted for. */
        deviceModelId: u.modelId,
        deviceUnitId: u.unitId,
      });
      /*
        THE TENDERS EVERY REGISTER TAKES, SEEDED AT CREATION.

        `provision` writes no payment types, so a new register came up taking
        NOTHING and step 3 below started empty. Cash was usually remembered.
        SPLIT WAS NOT — it is not a payment method anyone thinks to switch on,
        it is the ability to pay with two, so a register would go live unable
        to take half cash and half card and nobody found out until a customer
        asked at the counter.

        Only the two that need no device: both are `backed: false` in TENDERS,
        so neither depends on a reader being coupled, and seeding them can
        never write a tender this register cannot honour. Card is deliberately
        NOT seeded — which credit tender is right depends on the enrolled
        device class, which is the decision step 3 exists to make.

        Best-effort, and deliberately after the success toast: the register IS
        created at this point. A failure here leaves step 3 exactly as it was
        before — empty, and fixable by hand — so it must not be reported as a
        failure to create the register.
      */
      const seeded = ['cash', 'split'];
      try {
        /* No id back means there is nothing to seed against — treated as the
           failure it is rather than sent as `undefined` in the path. */
        if (!resp?.terminalId) throw new Error('no terminalId');
        await api.posNmi.putPaymentTypes(tenantId, resp.terminalId, {
          assigned: seeded,
          enabled: seeded,
        });
        toastService.success(`Register created — code ${resp?.provisionCode || ''} · takes cash and split`);
      } catch {
        toastService.success(`Register created — code ${resp?.provisionCode || ''}`);
        toastService.warning('Could not switch on cash and split for it — set them on the Payment types step.');
      }
      await load();
      onChanged();
    } catch (e: any) {
      toastService.error(e?.message || 'Could not create the register.');
    } finally {
      setBusy(null);
    }
  };

  if (!locationId) return <Problem text="This order has no location, so a register cannot be created for it." />;
  // A failed background re-read keeps the last good copy on screen; the
  // error box is only for a panel that has never loaded.
  if (error && terminals === null) return <Problem text={error} onRetry={load} />;
  if (terminals === null) return <Busy />;
  if (units.length === 0) return <p className="text-[12.5px] text-stone-500">No POS devices allocated to this order yet.</p>;

  const peeked = peek ? units.find((u) => u.unitId === peek) : undefined;

  return (
    <div className="flex flex-col gap-2">
      {/* Opened from the device row above. `units` is what this lane was handed
          — the same list the coupled partner is looked up in, so a pairing can
          be walked without leaving the card. */}
      {peeked && (
        <UnitQuickView
          unit={peeked}
          model={modelOf(models, peeked.modelId)}
          siblings={units}
          onClose={() => setPeek(null)}
          onOpenUnit={(id) => setPeek(id)}
        />
      )}
      <ForLocation tenantName={tenantName} locationName={locationName} locationId={locationId} what="Registers" />
      {units.map((u) => {
        const model = modelOf(models, u.modelId);
        const reg = registerFor(u);
        /*
          A REGISTER IS A CODE WITH AN EXPIRY, so it is not minted before the
          device that will claim it can claim it. That is the POS lane's own
          order — install RubyTill, work the checklist, THEN create the register
          — and the reason is said out loud below rather than hidden in a
          tooltip, because a disabled button with no visible cause reads as a
          bug.
        */
        const gate = registerGate(u, model, stageIds);
        const blocked = gate.reason;
        /*
          WHAT THIS REGISTER WILL BE, SHOWN BEFORE IT IS MINTED.

          The type is not a question put to the operator here — it is the
          catalog's answer for this device — but it decides whether the terminal
          has a customer display, so it is not allowed to be invisible either. A
          POS device whose catalog row answers nothing usable still provisions
          (as 'pos', which is what it would have been anyway), and SAYS so, with
          a link to the field that fixes it for every future register.
        */
        const willBe = provisionTypeForModel(model);
        const catalogSaidNothing = model?.category === 'pos_terminal'
          && !provisionTypeFromFormFactor(model?.appProfile?.terminalFormFactor);
        return (
          <Row
            key={u.unitId}
            model={model}
            title={model?.modelName || u.modelId}
            sub={label(u)}
            done={!!reg}
            onOpen={() => setPeek(u.unitId)}
          >
            {reg ? (
              <>
                <Chip tone="ok"><Check size={10} /> {reg.registerName}</Chip>
                {usableTerminalCode(reg) && reg.status !== 'activated' && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(usableTerminalCode(reg) as string);
                      setCopied(u.unitId);
                      setTimeout(() => setCopied(null), 1500);
                    }}
                    className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 font-mono text-[12px] font-bold tabular-nums text-amber-900 hover:bg-amber-100"
                  >
                    {copied === u.unitId ? <Check size={12} /> : <Copy size={12} />} {reg.provisionCode}
                  </button>
                )}
                {reg.status === 'activated' && <Chip tone="ok">Claimed</Chip>}
              </>
            ) : (
              <>
                {/* NAMED, not just labelled for screen readers. This is what
                    the register is called everywhere afterwards — on the till's
                    own sign-in, in reports, in the fleet list — and an unlabelled
                    box pre-filled with the model name reads as a read-only
                    caption rather than something you are meant to change. */}
                <label className="flex min-w-[224px] flex-col gap-1">
                  <span className="text-[9.5px] font-semibold uppercase tracking-wide text-stone-400">
                    Display name
                  </span>
                  <input
                    value={name[u.unitId] ?? u.registerName ?? `${model?.modelName || 'Register'} · ${label(u)}`}
                    onChange={(e) => {
                      setName((p) => ({ ...p, [u.unitId]: e.target.value }));
                      saveName(u.unitId, e.target.value);
                    }}
                    aria-label={`Display name for ${label(u)}`}
                    className="min-h-[36px] w-56 rounded-lg border border-stone-300 px-2 text-[12px] focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20"
                  />
                  {/* Quiet, and only after something was typed — a permanent
                      "Saved" on an untouched field claims a write that never
                      happened. */}
                  {nameSave[u.unitId] && (
                    <span className="flex items-center gap-1 text-[10px] font-semibold text-stone-400">
                      {nameSave[u.unitId] === 'saving'
                        ? <><RubyGemMark size={9} drawing /> Saving…</>
                        : <><Check size={9} className="text-emerald-600" /> Saved</>}
                    </span>
                  )}
                </label>
                {catalogSaidNothing ? (
                  <a
                    href={`/inventory/stock/${encodeURIComponent(u.modelId)}/edit`}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-900 hover:bg-amber-100"
                    title="Set the screen type on this device so its registers provision as what it is"
                  >
                    <AlertTriangle size={11} />
                    No screen type on this device — registers as a plain POS Terminal. Set it →
                  </a>
                ) : (
                  <Chip tone="ok"><Monitor size={10} /> {formFactorLabel(willBe)}</Chip>
                )}
                {!blocked ? (
                  <Action onClick={() => create(u)} busy={busy === u.unitId}>
                    <Plus size={12} /> Create register
                  </Action>
                ) : (
                  <UnlockGate gate={gate} />
                )}
              </>
            )}
          </Row>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   COUPLE — one POS device to one payment device, scannable both ways
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * THE WORK A PAIR CARRIES, supplied by whoever mounts this panel.
 *
 * Coupling is the moment the unit of work stops being a machine and becomes a
 * pair: from here on the handover, the tenders, the button layout and the run
 * that ties them together all act on the two devices as one. Those panels
 * already exist and are not touched — they are handed in as slots and drawn as
 * steps on the pair's card.
 *
 * SLOTS RATHER THAN IMPORTS, for one blunt reason: TillHandoverPanel imports
 * `ForLocation` from this file, so importing it back would close a module
 * cycle. The order screen already imports every one of these, so it is the
 * natural place to build them — and this panel stays presentational about work
 * it does not own.
 *
 * A slot is only CALLED when its step is opened, so four pairs on an order do
 * not mount four copies of a panel that reads every register on the tenant.
 */
export interface CoupleStepSlots {
  handover?: (pos: DeviceUnitRow, payment: DeviceUnitRow) => React.ReactNode;
  paymentTypes?: (pos: DeviceUnitRow, payment: DeviceUnitRow) => React.ReactNode;
  buttons?: (pos: DeviceUnitRow, payment: DeviceUnitRow) => React.ReactNode;
  /** Each device's own checklist for this stage. */
  checks?: (unit: DeviceUnitRow) => React.ReactNode;
}

export const DeviceCouplePanel: React.FC<{
  units: DeviceUnitRow[];
  models: DeviceModelRow[];
  posUnitIds: readonly string[];
  paymentUnitIds: readonly string[];
  /** The pair's own work, drawn as steps. Absent, the card is just the pair. */
  steps?: CoupleStepSlots;
  /* Both units come back from the writes, so the pair is patched in place
     rather than costing a whole-order refetch and the reader's place. */
  onUnitChanged: (unit: DeviceUnitRow) => void;
  /* WHAT THE RUNNER NEEDS. Coupling is the last question a person answers
     before a till can be set up end to end, so "Set up POS" is offered HERE,
     on the pair it acts on, rather than three steps further down. Absent these,
     the panel is exactly what it was and no runner is drawn. */
  tenantId?: string;
  locationId?: string;
  locationName?: string;
  ownerEmail?: string;
  onSetupDone?: () => void;
}> = ({
  units, models, posUnitIds, paymentUnitIds, onUnitChanged, steps,
  tenantId, locationId, locationName, ownerEmail, onSetupDone,
}) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [pick, setPick] = useState<Record<string, string>>({});
  const [scan, setScan] = useState('');
  const [hit, setHit] = useState<{ a: DeviceUnitRow; b: DeviceUnitRow } | null>(null);
  const [miss, setMiss] = useState(false);
  const scanRef = useRef<HTMLInputElement | null>(null);

  const byId = useMemo(() => new Map(units.map((u) => [u.unitId, u])), [units]);
  const posUnits = units.filter((u) => posUnitIds.includes(u.unitId));
  const payUnits = units.filter((u) => paymentUnitIds.includes(u.unitId));

  /* Already spoken for by SOMEONE ELSE. A device pointing at the till you are
     looking at is not taken — it is this pair. */
  const takenBy = (candidateId: string, forUnitId: string) =>
    units.find((u) => u.unitId !== forUnitId && u.coupledUnitId === candidateId && u.unitId !== candidateId);

  /**
   * BOTH SIDES, IN ONE GO.
   *
   * Writing only the till's link would leave the reader unable to answer
   * "what do I ship with this?" — which is the entire question the barcode
   * lookup exists for. If the second write fails the first is rolled back,
   * because a half-link is worse than none: it reads as coupled from one end.
   */
  const couple = async (pos: DeviceUnitRow, cardUnitId: string) => {
    const card = byId.get(cardUnitId);
    if (!card) return;
    setBusy(pos.unitId);
    try {
      const a = await hardwareApi.upsertUnit({ ...pos, coupledUnitId: card.unitId } as any);
      try {
        if (card.unitId !== pos.unitId) {
          const b = await hardwareApi.upsertUnit({ ...card, coupledUnitId: pos.unitId } as any);
          if (b?.unit) onUnitChanged(b.unit);
        }
      } catch (inner) {
        // `null`, not `undefined` — an undefined key never reaches the service,
        // so this rollback used to leave the first half pointing at a unit that
        // never pointed back.
        const undo = await hardwareApi.upsertUnit({ ...pos, coupledUnitId: null } as any).catch(() => null);
        if (undo?.unit) onUnitChanged(undo.unit);
        throw inner;
      }
      if (a?.unit) onUnitChanged(a.unit);
      toastService.success(`${label(pos)} and ${label(card)} now point at each other.`);
    } catch (e: any) {
      toastService.error(e?.message || 'Could not couple these devices.');
    } finally {
      setBusy(null);
    }
  };

  /*
    BREAKING A PAIR SENDS `null`, NOT `undefined`.

    `undefined` is dropped by JSON.stringify, so the body reached the service
    with no `coupledUnitId` key and its `{...existing, ...body}` merge kept the
    old value — a 200 with nothing changed, and a Break button that looked
    broken because the pair was still on screen afterwards. `null` is the
    service's explicit "clear this".

    Both halves point at each other, so both have to be cleared. The second
    write used to be `.catch(() => null)`: if it failed, the operator was told
    nothing and the pair was left half-broken — one unit free, the other still
    naming it. It is allowed to throw now and is reported like any other
    failure.
  */
  const uncouple = async (pos: DeviceUnitRow) => {
    const card = pos.coupledUnitId ? byId.get(pos.coupledUnitId) : undefined;
    setBusy(pos.unitId);
    try {
      const a = await hardwareApi.upsertUnit({ ...pos, coupledUnitId: null } as any);
      if (a?.unit) onUnitChanged(a.unit);
      if (card && card.unitId !== pos.unitId) {
        const b = await hardwareApi.upsertUnit({ ...card, coupledUnitId: null } as any);
        if (b?.unit) onUnitChanged(b.unit);
      }
      setHit(null);
      toastService.success(`${label(pos)} and ${label(card || pos)} are no longer a pair.`);
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not break this couple.');
    } finally {
      setBusy(null);
    }
  };

  const lookup = () => {
    const code = scan.trim().toUpperCase();
    setHit(null); setMiss(false);
    if (!code) return;
    const found = units.find((u) =>
      [u.trackingId, u.serialNumber, u.unitId].some((v) => String(v || '').toUpperCase() === code));
    const other = found?.coupledUnitId ? byId.get(found.coupledUnitId) : undefined;
    if (found && other) setHit({ a: found, b: other }); else setMiss(true);
    setScan('');
    scanRef.current?.focus();
  };

  if (posUnits.length === 0) {
    return <p className="text-[12.5px] text-stone-500">No POS devices allocated to this order yet.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {posUnits.map((pos) => {
        const posModel = modelOf(models, pos.modelId);
        const card = pos.coupledUnitId ? byId.get(pos.coupledUnitId) : undefined;
        const cardModel = card ? modelOf(models, card.modelId) : undefined;
        const options = payUnits.filter((c) => !takenBy(c.unitId, pos.unitId));

        /*
          AN UNCOUPLED TILL IS NOT A CARD TO OPEN.

          There is nothing inside it yet — no tenders to set, no layout to
          push, no run to make — so it stays the flat row it always was, with
          the picker on it. The card, and everything under it, appears the
          moment the pair exists.
        */
        const picker = (
          <div className="flex flex-wrap items-center gap-2">
            <SearchableDropdown
              options={options.map((c) => ({
                value: c.unitId,
                label: `${modelOf(models, c.modelId)?.modelName || c.modelId} · ${label(c)}`
                  + (c.unitId === pos.unitId ? ' (this device — all-in-one)' : ''),
              }))}
              value={pick[pos.unitId] || null}
              onChange={(v) => setPick((p) => ({ ...p, [pos.unitId]: v || '' }))}
              placeholder="Pick its payment device…"
              variant="light"
            />
            <Action
              onClick={() => couple(pos, pick[pos.unitId])}
              busy={busy === pos.unitId}
              disabled={!pick[pos.unitId]}
            >
              <Link2 size={12} /> Couple
            </Action>
          </div>
        );

        const header = (
          <CoupleHeader
            pos={pos}
            posModel={posModel}
            payment={card}
            paymentModel={cardModel}
            fallback={picker}
          />
        );

        if (!card) {
          return (
            <div
              key={pos.unitId}
              className="rounded-xl border border-dashed border-stone-300 bg-stone-50/60 p-3"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span className="min-w-0 flex-1">{header}</span>
                <StageChip tone="idle">Not coupled</StageChip>
              </div>
            </div>
          );
        }

        return (
          <Disclosure
            key={pos.unitId}
            tone="done"
            defaultOpen={posUnits.length === 1}
            header={header}
            chip={<StageChip tone="done">Coupled</StageChip>}
            actions={(
              <ReportProblemButton
                units={[pos, ...(card.unitId !== pos.unitId ? [card] : [])]}
                onUnitChanged={onUnitChanged}
                subtle
              />
            )}
          >
            <DisclosureBody className="space-y-2">
              {/*
                THE ONE PRESS, FIRST.

                Set up POS does the whole sequence by itself — handover,
                tenders, layout — so it is the first thing offered when a pair
                is opened, not the last. The steps below it are where you go
                when it failed, when something has to be set by hand, or when
                you want to see what it did; they are not a queue to work
                through before reaching it.
              */}
              {tenantId && (
                <PosSetupRunner
                  tenantId={tenantId}
                  locationId={locationId}
                  locationName={locationName}
                  ownerEmail={ownerEmail}
                  unit={pos}
                  unitLabel={label(pos)}
                  coupledSerial={card.serialNumber || undefined}
                  onDone={() => onSetupDone?.()}
                />
              )}

              {/*
                THE SAME WORK, STEP BY STEP.

                Each is one line until it is opened, and what opens is the
                panel that already owns that work — handed in as a slot, drawn
                here, unchanged. These are the ONLY place this work now lives:
                the till_handover, payment_types and button_layout stages no
                longer draw their own copies.
              */}
              {steps?.handover && (
                <StepRow label="Hand the till over" state="pending"
                  summary="The owner's role, their PIN, and the card that goes in the box.">
                  {steps.handover(pos, card)}
                </StepRow>
              )}
              {steps?.paymentTypes && (
                <StepRow label="Payment types, signature and tips" state="pending"
                  summary="What this register takes, and what hangs off each tender.">
                  {steps.paymentTypes(pos, card)}
                </StepRow>
              )}
              {steps?.buttons && (
                <StepRow label="Button layout — Available Actions" state="pending"
                  summary="What this till MAY show. Set up POS installs the default from it.">
                  {steps.buttons(pos, card)}
                </StepRow>
              )}

              {/* Each machine's own checklist for this stage, under the pair
                  rather than in a tree further down the page. */}
              {steps?.checks && (
                <>
                  <div>{steps.checks(pos)}</div>
                  {card.unitId !== pos.unitId && <div>{steps.checks(card)}</div>}
                </>
              )}

              <div className="flex justify-end pt-0.5">
                <button
                  onClick={() => uncouple(pos)}
                  disabled={busy === pos.unitId}
                  className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-red-200 bg-white px-2.5 text-[11.5px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                >
                  {busy === pos.unitId ? <RubyGemMark size={12} drawing color="currentColor" /> : <Link2Off size={12} />} Break
                </button>
              </div>
            </DisclosureBody>
          </Disclosure>
        );
      })}

      {/* ── the lookup: scan either barcode, get both halves ─────────────── */}
      <div className="rounded-xl border border-stone-200 bg-white p-3">
        <p className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.08em] text-stone-500">
          <ScanLine size={12} /> Scan any device
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={scanRef}
            value={scan}
            onChange={(e) => setScan(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookup(); } }}
            placeholder="RTA-000016"
            autoComplete="off"
            spellCheck={false}
            aria-label="Scan a device to find its pair"
            className="min-h-[40px] flex-1 min-w-[10rem] rounded-lg border border-stone-300 px-3 font-mono text-[13px]"
          />
          <button
            onClick={lookup}
            className="min-h-[40px] rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-600 hover:bg-stone-50"
          >
            Look up
          </button>
        </div>

        {hit && (
          <div className="mt-2 rounded-lg border-l-[3px] border-emerald-500 bg-emerald-50 px-3 py-2 text-[12.5px] text-emerald-900">
            <strong className="font-mono">{label(hit.a)}</strong> ships with{' '}
            <strong className="font-mono">{label(hit.b)}</strong>
            {' — '}{modelOf(models, hit.b.modelId)?.modelName || hit.b.modelId}.
            <span className="mt-1 block text-emerald-800">
              Both boxes go together. Packing either on its own is what this lookup exists to stop.
            </span>
          </div>
        )}
        {miss && (
          <div className="mt-2 rounded-lg border-l-[3px] border-red-500 bg-red-50 px-3 py-2 text-[12.5px] text-red-800">
            Nothing on this order carries that code, or it is not coupled yet.
          </div>
        )}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PAYMENT TYPES — the coupled device is the assignment, so it is not asked twice
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * The tick / number at the head of a step in a device's chain.
 *
 * Done steps collapse to a fact; the rest keep their number so the ORDER the
 * work has to happen in stays visible even when nothing in it is finished.
 */
const StepMark: React.FC<{ done: boolean; n: number; muted?: boolean }> = ({ done, n, muted }) => (
  <span
    className={`mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full text-[10px] font-bold ${
      done
        ? 'bg-emerald-500 text-white'
        : muted
          ? 'border border-stone-300 bg-white text-stone-400'
          : 'bg-ruby text-white'
    }`}
  >
    {done ? '✓' : n}
  </span>
);

type PaymentTypesPanelProps = {
  tenantId: string;
  /** Tips are granted per LOCATION, on the gateway config — not per register. */
  locationId?: string;
  /** Named on the location rows, so a location-wide grant never reads as per-device. */
  locationName?: string;
  /** For the broadcast option's "All N tills in <tenant>". */
  tenantName?: string;
  units: DeviceUnitRow[];
  /** For the model name in each card's header. Optional — the card degrades to the id. */
  models?: DeviceModelRow[];
  posUnitIds: readonly string[];
  /**
   * BUMPED BY THE ORDER SCREEN ON EVERY REFRESH.
   *
   * Surcharge is APPROVED on another lane of the same page. Without this the
   * panel read the gateway once on mount and never again, so a location
   * approved a moment ago still read "not approved" here.
   */
  refreshKey?: number;
  onChanged: () => void;
};

/**
 * ONE SAVE BAR PER PAGE. Inside a `PaymentTypesSaveHub` (the order page wraps
 * every pair in one) this card's drafts live in the hub and the hub draws the
 * one bar. Used on its own, it brings its own hub — still exactly one bar.
 */
export const PaymentTypesPanel: React.FC<PaymentTypesPanelProps> = (props) => {
  const hub = usePaymentTypesHub();
  if (hub) return <PaymentTypesPanelBody {...props} />;
  return (
    <PaymentTypesSaveHub
      tenantId={props.tenantId}
      locationId={props.locationId}
      locationName={props.locationName}
      tenantName={props.tenantName}
      onSaved={props.onChanged}
      showLocationValues
    >
      <PaymentTypesPanelBody {...props} />
    </PaymentTypesSaveHub>
  );
};

const PaymentTypesPanelBody: React.FC<PaymentTypesPanelProps> = ({
  tenantId, locationId, locationName, units, models, posUnitIds, refreshKey, onChanged,
}) => {
  const dataVersion = useDataVersion();
  const hub = usePaymentTypesHub()!;
  const { draft, base, gateway, grants, setGrants, setTillDraft, saving } = hub;
  const [terminals, setTerminals] = useState<any[] | null>(null);
  /*
    ONE CARD PER PHYSICAL TILL — coupled → register → what it takes — and
    "what it takes" is the SAME block the terminal's Payment Types tab draws
    (RegisterPaymentTypes). Nothing saves on toggle: every change waits for
    the page's one save bar (the hub), and a save sends only what changed.
    That is what stops this card switching off an emergency override or
    re-enabling tenders the merchant turned off.
  */
  const [error, setError] = useState<string | null>(null);
  /** Deregister in flight, by terminalId. */
  const [busy, setBusy] = useState<string | null>(null);
  /**
   * THE LOCATION'S REGISTERED CARD READERS. A reader-backed tender binds a
   * POI device by its EMV pool id (`emv_…`), not the coupled inventory unit id.
   */
  const [emvDevices, setEmvDevices] = useState<any[] | null>(null);
  /** The register awaiting a deregister confirmation, or null. */
  const [deregistering, setDeregistering] = useState<any | null>(null);

  // The hub draws the save bar only while at least one card is on the page.
  useEffect(() => hub.attach(), []); // eslint-disable-line react-hooks/exhaustive-deps

  const posUnits = units.filter((u) => posUnitIds.includes(u.unitId));

  /**
   * WHICH REGISTERED READER IS THE ONE COUPLED TO THIS TILL — matched on
   * SERIAL NUMBER ONLY; anything but one exact match returns null and
   * `emvProblemFor` says which serial it looked for.
   */
  const emvMatchFor = (unit: DeviceUnitRow) => {
    const coupled = units.find((x) => x.unitId === unit.coupledUnitId);
    return readerBySerial(emvDevices || [], coupled?.serialNumber);
  };
  const emvRecordFor = (unit: DeviceUnitRow): any | null => emvMatchFor(unit).device;
  const emvProblemFor = (unit: DeviceUnitRow): string | undefined => emvMatchFor(unit).problem;

  const load = useCallback(async () => {
    setError(null);
    try {
      const res: any = await api.posTerminals.list(tenantId);
      const list: any[] = res?.terminals || [];
      setTerminals(list);
      const mine = list.filter((t) => posUnits.some((u) => registerForUnit(list, u)?.terminalId === t.terminalId));
      const [pts, pool] = await Promise.all([
        Promise.all(mine.map((t) => api.posNmi.getPaymentTypes(tenantId, t.terminalId).catch(() => null))),
        locationId
          ? api.posNmi.listEmvDevicesAtLocation(tenantId, locationId)
            .then((r: any) => r?.devices || r?.items || (Array.isArray(r) ? r : []))
            .catch(() => [])
          : Promise.resolve([]),
      ]);
      setEmvDevices(pool);
      /* The GET says whether surcharge and debit are STORED or following
         (`*Stored`). Only an older GET without them needs the raw register
         rows (ext2) as the fallback. */
      const needRaw = pts.some((pt: any) => !hasStoredFlags(pt));
      const regs: any = needRaw && api.posConfig?.listRegisters
        ? await api.posConfig.listRegisters(tenantId).catch(() => null)
        : null;
      const rows: Record<string, RegisterState> = {};
      const names: Record<string, string> = {};
      mine.forEach((t, i) => {
        const raw = (regs?.registers || []).find((r: any) => r.registerId === t.terminalId || r.terminalId === t.terminalId) || null;
        const bound = (pool as any[]).find((d) => d.registerId === t.terminalId)?.deviceId || null;
        rows[t.terminalId] = registerFromResponses(pts[i] || {}, raw, bound);
        names[t.terminalId] = t.registerName || t.terminalId;
      });
      hub.ingest(rows, names);
      if (locationId) {
        try {
          /* THE CONFIG IS AT THE ROOT — `gateway` on it is the PROVIDER name. */
          const gw: any = await api.posNmi.getPaymentGateway(tenantId, locationId);
          hub.ingestGateway(gw ?? null);
        } catch { /* stays unread — rows that need it lock */ }
      }
    } catch (e: any) {
      setError(e?.message || 'Could not read the registers.');
    }
    // posUnits is derived from props each render; the ids are what matter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, locationId, posUnitIds.join(','), units.length, refreshKey]);

  // dataVersion: re-read after any save on the order page
  useEffect(() => { load(); }, [load, dataVersion]);

  /** How far this device has got: coupled → register → takes something. */
  const stepsDone = (unit: DeviceUnitRow, terminal: any, chosen: string[]) =>
    [!!unit.coupledUnitId, !!terminal, chosen.length > 0].filter(Boolean).length;

  /**
   * GIVE THE CODE BACK — soft, never `release`. The SAME code can be typed in
   * again, and the register survives with everything configured during testing.
   */
  const deregister = async (terminal: any) => {
    setBusy(terminal.terminalId);
    try {
      const resp: any = await api.posTerminals.deregister(terminal.terminalId, tenantId);
      setDeregistering(null);
      /* The list masks a claimed till's code (RT-****-AB12) — the full one only
         comes back from deregister, re-armed for 14 days. */
      const code = usableTerminalCode({ provisionCode: resp?.provisionCode });
      toastService.success(code
        ? `${terminal.registerName} deregistered. Type code ${code} on whichever device takes its place${resp?.codeExpiresAt ? ` — works once, expires ${formatExpiry(resp.codeExpiresAt)}` : ''}.`
        : `${terminal.registerName} deregistered. Use "Generate new code" on the Terminals list for an activation code.`);
      await load();
      onChanged();
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not deregister that till.');
    } finally {
      setBusy(null);
    }
  };

  // A failed background re-read keeps the last good copy on screen; the
  // error box is only for a panel that has never loaded.
  if (error && terminals === null) return <Problem text={error} onRetry={load} />;
  if (terminals === null) return <Busy />;

  const rows = posUnits.map((u) => ({ unit: u, terminal: registerForUnit(terminals, u) }));
  if (rows.length === 0) return <p className="text-[12.5px] text-stone-500">No POS devices on this order.</p>;

  return (
    <div className="flex flex-col gap-3">
      {rows.map(({ unit, terminal }) => {
        const model = modelOf(models || [], unit.modelId);
        const coupled = units.find((x) => x.unitId === unit.coupledUnitId);
        const tillBase = terminal ? base[terminal.terminalId] : undefined;
        const tillDraft = terminal ? draft[terminal.terminalId] : undefined;
        const chosen = tillBase?.assigned || [];
        const done = stepsDone(unit, terminal, chosen);
        /*
          CLAIMED, not merely created. `registerStatus` is the REGISTER# row —
          null until a till actually redeems the provisioning code.
        */
        const claimed = !!terminal?.registerStatus;
        const emv = emvRecordFor(unit);
        const method = emv ? (DEVICE_CLASS_TO_METHOD[String(emv.deviceClass || '')] || null) : null;
        const creditBlocked = !unit.coupledUnitId
          ? 'Couple its card reader first.'
          : emvDevices === null
            ? 'Reading the location’s card readers…'
            : !emv
              ? (emvProblemFor(unit) || 'The coupled reader is not registered as a card reader at this location yet — register it on the card processing step.')
              : !method
                ? unknownReaderMessage({ deviceClass: emv.deviceClass, deviceName: 'The coupled reader' })
                : null;

        return (
          <article key={unit.unitId} className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
            {/* WHICH BOX THIS IS — the card is headed by the physical device,
                because that is the thing an operator has in their hands. */}
            <header className="flex items-center gap-3 border-b border-stone-200 bg-stone-50/70 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-[13px] font-bold text-stone-900">{label(unit)}</span>
                  {unit.state && (
                    <span className="rounded-full bg-stone-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-stone-600">
                      {UNIT_STATUS_LABEL[unit.state] || unit.state}
                    </span>
                  )}
                </div>
                <p className="truncate text-[11.5px] text-stone-500">
                  {model?.modelName || 'Unknown model'}{unit.serialNumber ? ` · ${unit.serialNumber}` : ''}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <div className="text-[10.5px] font-bold uppercase tracking-wide text-stone-400">Setup</div>
                <div className={`text-[15px] font-extrabold ${done === 3 ? 'text-emerald-600' : 'text-ruby'}`}>{done} of 3</div>
              </div>
            </header>

            <ol className="divide-y divide-stone-100">
              {/* ── 1. COUPLED ─────────────────────────────────────────────── */}
              <li className="flex items-start gap-3 px-4 py-3">
                <StepMark done={!!unit.coupledUnitId} n={1} />
                <div className="min-w-0 flex-1">
                  <div className={`text-[12.5px] font-bold ${unit.coupledUnitId ? 'text-stone-800' : 'text-stone-500'}`}>
                    Coupled to a card reader
                  </div>
                  <div className="text-[11.5px] text-stone-500">
                    {coupled
                      ? <>{modelOf(models || [], coupled.modelId)?.modelName || 'Reader'} · <span className="font-mono">{label(coupled)}</span> — every card tender below uses this reader.</>
                      : 'Not coupled — do it on the Couple devices step. Card tenders stay locked until then.'}
                  </div>
                  {/* Serial-only binding: when no single pool reader matches,
                      say which serial was looked for rather than guess. */}
                  {coupled && emvDevices !== null && emvProblemFor(unit) && (
                    <p className="mt-1 text-[11.5px] font-medium text-amber-700">{emvProblemFor(unit)}</p>
                  )}
                </div>
              </li>

              {/* ── 2. REGISTER ────────────────────────────────────────────── */}
              <li className="flex items-start gap-3 px-4 py-3">
                <StepMark done={!!terminal} n={2} />
                <div className="min-w-0 flex-1">
                  <div className={`text-[12.5px] font-bold ${terminal ? 'text-stone-800' : 'text-stone-500'}`}>Register created</div>
                  {terminal ? (
                    <>
                      {/*
                        THE REGISTER, AND THE BOX IT WAS MINTED FOR.

                        A register name on its own ("2nd Vector") does not say
                        which machine in the warehouse carries it, and that is
                        the thing somebody is holding when they read this. The
                        provision call has always written `deviceUnitId` — this
                        resolves it back to the device's own name and tracking
                        id, falling back to the unit this row is for, which is
                        the device the register belongs to anyway.
                      */}
                      <div className="truncate text-[11.5px] text-stone-500">
                        {terminal.registerName}
                        {(() => {
                          const box = units.find((x) => x.unitId === terminal.deviceUnitId) || unit;
                          const boxModel = modelOf(models || [], box.modelId);
                          return (
                            <>
                              {' · '}{boxModel?.modelName || box.modelId}
                              {' '}<span className="font-mono text-stone-400">{label(box)}</span>
                            </>
                          );
                        })()}
                      </div>
                      <div className="mt-1.5 flex flex-wrap items-center gap-2">
                        {terminal.provisionCode && (
                          <code className="rounded-md border border-stone-200 bg-stone-50 px-2 py-1 text-[11.5px] font-bold tracking-wider text-stone-700">
                            {terminal.provisionCode}
                          </code>
                        )}
                        {claimed
                          ? <Chip tone="ok">claimed by the till</Chip>
                          : (
                            <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10.5px] font-bold text-amber-700">
                              code not claimed yet
                            </span>
                          )}
                        {/*
                          WHEN IT WAS CLAIMED — the fact behind the chip.

                          "claimed by the till" says it happened and not when,
                          which is the first thing asked when a device is
                          swapped or a code is re-sent. `activatedAt` is the
                          stamp the terminal's own details tab reads.
                        */}
                        {claimed && terminal.activatedAt && (
                          <span className="text-[11px] text-stone-500">{when2(terminal.activatedAt)}</span>
                        )}
                      </div>

                      {/*
                        UNCLAIM IT, FROM HERE.

                        A device that has claimed a code sometimes has to give
                        it back — the wrong box was built, a unit failed on the
                        bench, a customer is being moved to a different
                        terminal. This is the SOFT deregister
                        (`posTerminals.deregister`, activated → pending), not
                        `release`: the code stays valid, and the register keeps
                        every payment type, tip setting and button layout
                        configured during testing. Releasing would hand the
                        customer a fresh, empty terminal and throw the testing
                        away.

                        Offered only once a till has actually claimed — there
                        is nothing to undo before that — and behind a
                        confirmation, because the device stops trading the
                        moment it lands.
                      */}
                      {claimed && (
                        <div className="mt-2">
                          <button
                            onClick={() => setDeregistering(terminal)}
                            disabled={busy === terminal.terminalId}
                            className="inline-flex min-h-[34px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-2.5 text-[11px] font-semibold text-stone-600 hover:border-amber-300 hover:bg-amber-50 hover:text-amber-800 disabled:opacity-50"
                          >
                            {busy === terminal.terminalId
                              ? <RubyGemMark size={11} drawing color="currentColor" />
                              : <RotateCcw size={11} />}
                            Deregister this till
                          </button>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="text-[11.5px] text-stone-500">
                      Not created — do it on the Create register step. Coupling a reader does not create one.
                    </div>
                  )}
                </div>
              </li>

              {/* ── 3. WHAT IT TAKES ───────────────────────────────────────── */}
              <li className={`px-4 py-3 ${terminal && !chosen.length ? 'bg-[#FFF1F3]/60' : ''}`}>
                <div className="flex items-start gap-3">
                  <StepMark done={chosen.length > 0} n={3} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`text-[12.5px] font-bold ${chosen.length ? 'text-stone-800' : 'text-stone-900'}`}>
                        What this register takes
                      </span>
                      {terminal && !chosen.length && (
                        <span className="rounded-full bg-ruby px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">next</span>
                      )}
                      {chosen.length > 0 && <Chip tone="ok">{chosen.length} assigned</Chip>}
                    </div>
                    <div className="text-[11.5px] text-stone-500">
                      {!terminal
                        ? 'Waiting on the register above.'
                        : !chosen.length
                          ? 'Nothing assigned yet — the till cannot complete a sale until one of these is on.'
                          : 'Below — the same as the terminal’s Payment Types tab.'}
                    </div>
                  </div>
                </div>
              </li>
            </ol>
            {terminal && tillDraft && (
              <div className="border-t border-stone-200 bg-stone-50/60 p-3">
                <RegisterPaymentTypes
                  draft={tillDraft}
                  onChange={(next) => setTillDraft(terminal.terminalId, next)}
                  grants={grants}
                  onGrantsChange={setGrants}
                  gateway={gateway}
                  locationName={locationName}
                  reader={readerInfoFor(emv)}
                  onAssignCredit={() => {
                    if (creditBlocked || !emv || !method) { toastService.error(creditBlocked || 'No reader to assign.'); return; }
                    setTillDraft(terminal.terminalId, assignTender(tillDraft, method, emv.deviceId));
                  }}
                  creditBlocked={creditBlocked}
                  approvalWhere="approve it on the card processing step"
                  disabled={saving}
                  showLocationCard={false}
                  creditDevicePanel={emv && isRtpayDevice(emv) && tillDraft.emvDeviceId === emv.deviceId
                    ? <RubyTillPayDevicePanel tenantId={tenantId} device={emv} disabled={saving} />
                    : null}
                />
              </div>
            )}
          </article>
        );
      })}

      {/* The merchant's location values are drawn ONCE, by the hub, under
          every till on the page — not once per card. */}

      <p className="rounded-xl border-l-[3px] border-amber-400 bg-amber-50 px-3 py-2 text-[12px] text-amber-900">
        Which tenders a register may take is a business decision, so this step stays a human one even once the rest is automated.
      </p>


      {/*
        WHAT DEREGISTERING COSTS, BEFORE IT HAPPENS.

        The device stops trading the moment this lands, so the two facts that
        decide whether that is acceptable — the code still works, the
        configuration survives — are said here rather than discovered after.
      */}
      {deregistering && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setDeregistering(null)}
        >
          <div
            className="w-full max-w-[480px] overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-stone-200 px-5 py-4">
              <h3 className="text-[15px] font-bold text-stone-900">Deregister this till?</h3>
              <p className="mt-1 text-[12.5px] text-stone-500">{deregistering.registerName}</p>
            </div>
            <div className="px-5 py-4 text-[12.5px] leading-relaxed text-stone-600">
              The device stops trading straight away and goes back to asking for a code.
              <ul className="ml-4 mt-2 list-disc space-y-1">
                <li>
                  {usableTerminalCode(deregistering)
                    ? <>Code{' '}<span className="font-mono font-bold text-stone-800">{usableTerminalCode(deregistering)}</span>{' '}works on whichever device takes its place.</>
                    : 'Its activation code is shown when this is done — type it on whichever device takes its place. It works once and expires in 14 days.'}
                </li>
                <li>
                  The register keeps its payment types, tips, signature rules and button layout. Nothing
                  configured during testing is lost.
                </li>
              </ul>
              <p className="mt-2 text-stone-500">
                This is not the same as releasing the code, which ends the register for good.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-stone-200 px-5 py-3.5">
              <button
                onClick={() => setDeregistering(null)}
                className="min-h-[44px] rounded-xl border border-stone-200 px-4 py-2.5 text-sm font-semibold text-stone-700 hover:bg-stone-50"
              >
                Cancel
              </button>
              <button
                onClick={() => deregister(deregistering)}
                disabled={busy === deregistering.terminalId}
                className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {busy === deregistering.terminalId && <RubyGemMark size={13} drawing color="currentColor" />}
                Deregister
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   BUTTON LAYOUT — a starting screen, so nobody opens an empty till
   ═══════════════════════════════════════════════════════════════════════════ */

export const ButtonLayoutPanel: React.FC<{
  tenantId: string;
  units: DeviceUnitRow[];
  posUnitIds: readonly string[];
  onChanged: () => void;
}> = ({ tenantId, units, posUnitIds, onChanged }) => {
  const [terminals, setTerminals] = useState<any[] | null>(null);
  const dataVersion = useDataVersion();
  const [error, setError] = useState<string | null>(null);

  const posUnits = units.filter((u) => posUnitIds.includes(u.unitId));

  const load = useCallback(async () => {
    setError(null);
    try {
      const res: any = await api.posTerminals.list(tenantId);
      setTerminals(res?.terminals || []);
    } catch (e: any) {
      setError(e?.message || 'Could not read the registers.');
    }
  }, [tenantId]);

  // dataVersion: re-read after any save on the order page
  useEffect(() => { load(); }, [load, dataVersion]);

  // A failed background re-read keeps the last good copy on screen; the
  // error box is only for a panel that has never loaded.
  if (error && terminals === null) return <Problem text={error} onRetry={load} />;
  if (terminals === null) return <Busy />;

  const rows = posUnits.map((u) => ({
    unit: u,
    terminal: registerForUnit(terminals, u),
  })).filter((r) => r.terminal);

  if (rows.length === 0) {
    return <p className="text-[12.5px] text-stone-500">No registers yet — create them on the POS devices lane first.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {rows.map(({ terminal }) => (
        <PosButtonLayoutTab
          key={terminal.terminalId}
          terminal={{
            terminalId: terminal.terminalId,
            locationId: terminal.locationId,
            registerName: terminal.registerName,
          }}
          tenantId={tenantId}
          onSaved={onChanged}
        />
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   EMV DEVICE CONFIGURATION — the three things done ON the terminal, per device
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * WHY THIS STAGE HAD NO PANEL, and why it needed one.
 *
 * `configure_payment` resolved from the TID alone and drew nothing — so the
 * three jobs somebody physically does on a terminal (install the app, set its
 * parameters up in the portal, make the terminal pull them down) lived on no
 * screen at all. They existed as two ORDER-level journey ticks, which is the
 * wrong shape twice over: there were two of them, not three, and one tick
 * covering every reader on an order says nothing about any individual one. Two
 * readers are two separate pieces of work.
 *
 * So they are per DEVICE and they live on the device row, written once it is in
 * the pool — which is also the only point at which there is a device row to
 * write them to.
 *
 * Nothing here can observe any of the three. Each is a person's assertion,
 * shown as who and when, never as a fact.
 */
const CONFIG_STEPS: { key: 'appInstalled' | 'paramsSetup' | 'paramsDownloaded'; label: string; hint: string }[] = [
  {
    key: 'appInstalled',
    label: 'Update and install the app',
    hint: 'The payment application updated and installed on the terminal itself. Nothing below can be done on a terminal still running an old build.',
  },
  {
    key: 'paramsSetup',
    label: 'Setup parameters',
    hint: 'The terminal’s profile built in the processor’s portal — SPIn mode, tip and receipt options.',
  },
  {
    key: 'paramsDownloaded',
    label: 'Download parameters',
    hint: 'The terminal pulls that profile down over the network. Until it has, it is not programmed — whatever the portal says.',
  },
];

const when2 = (ts?: number) =>
  ts ? new Date(ts).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

/**
 * THE POOL COMES FROM THE ORDER SCREEN, not from a second fetch of its own.
 *
 * This panel used to load the pool once on mount and never again. Registering a
 * device happens in ANOTHER panel, so nothing told this one to look — the stage
 * kept showing "register them above first" until the page was reloaded, on an
 * order where the device was plainly in the pool.
 *
 * The order screen already holds the same list and already refreshes it in
 * `refreshSetup` — the very callback registration fires. Reading it from there
 * removes the duplicate request AND the staleness in one move: there is now one
 * copy of this data on the screen, so there is nothing left to disagree.
 */
export const EmvConfigStepsPanel: React.FC<LaneProps & {
  /** The location's device pool, owned and refreshed by the order screen.
   *  `null` means it has not come back yet — distinct from an empty pool. */
  pool: any[] | null;
}> = ({
  tenantId, tenantName, locationId, locationName, units, models, onChanged, pool,
}) => {
  const [busy, setBusy] = useState<string | null>(null);

  const toggle = async (deviceId: string, step: typeof CONFIG_STEPS[number], done: boolean) => {
    setBusy(`${deviceId}:${step.key}`);
    try {
      await api.posNmi.updateEmvDevice(tenantId, locationId!, deviceId, {
        configStep: step.key, configStepDone: done,
      });
      /* The order screen re-reads the pool, and this panel re-renders from it.
         No local reload, because there is no local copy. */
      onChanged();
    } catch (e: any) {
      /* The service refuses an out-of-order tick and says which step is
         missing — pass its sentence through rather than "failed". */
      toastService.error(e?.serverMessage || 'Could not record that step.');
    } finally {
      setBusy(null);
    }
  };

  if (!locationId) return <Problem text="This order has no location, so there are no devices to configure." />;
  /*
    `null` is "the order screen has not got the pool back". That is normally a
    moment, but its fetch swallows failures by design — the lane reads as
    not-yet-known rather than wrongly empty — so it can also be for ever. A
    spinner that never stops says nothing; this offers the retry instead, and
    `onChanged` is the same refresh the rest of the screen uses.
  */
  if (pool === null) {
    return <Problem text="Still reading this location’s device pool." onRetry={onChanged} />;
  }

  /* Only the readers ON THIS ORDER, matched by serial the way the register
     panel does — the pool belongs to the location and carries every device at
     it, including ones from other orders that are nobody's business here. */
  const serials = new Set(units.map((u) => String(u.serialNumber || '').trim()).filter(Boolean));
  const mine = (pool || []).filter((d) => serials.has(String(d.serialNumber || '').trim()));

  if (mine.length === 0) {
    return (
      <p className="text-[12.5px] text-stone-500">
        No card device from this order is in the location’s pool yet — register them above first.
        These three steps are recorded against a device, so there has to be one.
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <ForLocation tenantName={tenantName} locationName={locationName} locationId={locationId} what="Device pools" />

      {mine.map((dev) => {
        const unit = units.find((u) => String(u.serialNumber || '').trim() === String(dev.serialNumber || '').trim());
        const model = modelOf(models, unit?.modelId);
        const steps = dev.configSteps || {};
        const doneCount = CONFIG_STEPS.filter((s) => steps[s.key]).length;
        const allDone = doneCount === CONFIG_STEPS.length;

        return (
          <div key={dev.deviceId} className="rounded-xl border border-stone-200 bg-white p-3">
            <Row
              model={model}
              title={model?.modelName || dev.model || dev.deviceName}
              sub={`${unit ? label(unit) : dev.deviceName} · ${dev.serialNumber}`}
              done={allDone}
            >
              <Chip tone={allDone ? 'ok' : 'wait'}>
                {allDone ? <Check size={10} /> : null} {doneCount} of {CONFIG_STEPS.length}
              </Chip>
            </Row>

            <div className="mt-2 flex flex-col gap-1.5">
              {CONFIG_STEPS.map((step, i) => {
                const rec = steps[step.key];
                /* A step opens only once the one before it is done — the same
                   rule the service enforces, drawn rather than discovered by
                   pressing something that then refuses. */
                const blockedBy = CONFIG_STEPS.slice(0, i).find((p) => !steps[p.key]);
                const busyHere = busy === `${dev.deviceId}:${step.key}`;
                return (
                  <div
                    key={step.key}
                    className={`flex items-start gap-3 rounded-lg border px-3 py-2.5 ${
                      rec ? 'border-emerald-200 bg-emerald-50/60'
                        : blockedBy ? 'border-stone-200 bg-stone-50/60' : 'border-stone-200 bg-white'}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[12.5px] font-semibold text-stone-900">
                        <span className="mr-1.5 text-stone-400">{i + 1}.</span>{step.label}
                      </p>
                      <p className="mt-0.5 text-[11.5px] leading-relaxed text-stone-600">{step.hint}</p>
                      {rec ? (
                        <p className="mt-1 text-[11px] font-semibold text-emerald-700">
                          Marked done by {rec.by || 'someone'}{rec.at ? ` · ${when2(rec.at)}` : ''}
                        </p>
                      ) : blockedBy ? (
                        <p className="mt-1 text-[11px] text-stone-500">
                          Waiting on “{blockedBy.label}” — these happen in order.
                        </p>
                      ) : null}
                    </div>
                    {busyHere
                      ? <span className="inline-flex mt-1"><RubyGemMark size={16} drawing /></span>
                      : (
                        <Toggle
                          on={!!rec}
                          disabled={!!blockedBy || busy !== null}
                          onChange={(v) => toggle(dev.deviceId, step, v)}
                        />
                      )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};
